<template>
    <div class="quiz-single-question my-auto d-flex flex-column" ref="quizContainer">
        <v-container v-if="loadingQuiz">
            <v-row justify="center">
                <v-col cols="12" md="8" class="text-center">
                    <v-progress-circular indeterminate color="primary" size="64" width="5"></v-progress-circular>
                    <p class="mt-4 text-h6">Cargando cuestionario...</p>
                </v-col>
            </v-row>
        </v-container>

        <template v-else>
            <!-- Mostrar resultados cuando el cuestionario esté completado -->
            <div v-if="isQuizFinished || showResultsParam" class="quiz-results">
                <!-- Vista de revisión de respuestas -->
                <template v-if="reviewMode">
                    <div class="text-center mb-6">
                        <h1 class="text-h4 font-weight-bold mb-3">Revisión de respuestas</h1>
                        <h2 class="text-h5 primary--text mb-3">Has acertado {{ correctAnswers }} / {{
                            allQuestions.length }} ({{ scorePercentage }}%)</h2>
                    </div>

                    <div class="review-questions-container">
                        <div v-for="(question, index) in allQuestions" :key="index" class="review-question-card mb-4">
                            <div class="d-flex align-center">
                                <div class="question-result-icon mr-3">
                                    <svg v-if="questionResults[index]" xmlns="http://www.w3.org/2000/svg" width="20"
                                        height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                        class="lucide lucide-check text-success">
                                        <path d="M20 6 9 17l-5-5" />
                                    </svg>
                                    <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                        viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                                        stroke-linecap="round" stroke-linejoin="round"
                                        class="lucide lucide-x text-error">
                                        <path d="M18 6 6 18" />
                                        <path d="m6 6 12 12" />
                                    </svg>
                                </div>
                                <div class="question-text font-rubik" v-html="question.question"></div>
                                <v-btn icon small @click="toggleQuestionDetails(index)" class="ml-auto">
                                    <v-icon>{{ expandedQuestions[index] ? 'mdi-chevron-up' : 'mdi-chevron-down'
                                    }}</v-icon>
                                </v-btn>
                            </div>

                            <v-expand-transition>
                                <div v-if="expandedQuestions[index]" class="question-details mt-3 ml-10"
                                    :class="$vuetify.breakpoint.xs ? 'flex-column' : ''">
                                    <div class="mb-2">
                                        <span class="font-weight-medium">Tu respuesta:</span>
                                        <span :class="questionResults[index] ? 'text-success' : 'text-error'">
                                            {{ getAnswerForQuestion(index) || 'Sin respuesta' }}
                                        </span>
                                    </div>
                                    <div v-if="!questionResults[index]" class="mb-2">
                                        <span class="font-weight-medium">Respuesta correcta:</span>
                                        <span class="text-success">{{ question.answer }}</span>
                                    </div>
                                    <div v-if="question.explanation" class="explanation-container mt-2">
                                        <span class="font-weight-bold">Explicación:</span>
                                        <span :class="{ 'blurred-text': shouldBlurExplanation() && !userIsPremium }"
                                            style="color: #424242 !important;">{{ question.explanation }}</span>
                                        <div v-if="shouldBlurExplanation() && !userIsPremium"
                                            class="premium-explanation-overlay d-flex">
                                            <v-icon small color="grey darken-1" class="mr-1">mdi-lock-outline</v-icon>
                                            <span>Explicación disponible con Premium.</span>
                                            <v-btn small color="primary" class="ml-2 upgrade-btn-blur"
                                                @click="goToPremium">Ver más</v-btn>
                                        </div>
                                    </div>
                                </div>
                            </v-expand-transition>
                        </div>
                    </div>

                    <div class="d-flex justify-center mt-6">
                        <v-btn class="action-btn regular-btn font-rubik" depressed @click="retryQuiz">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="mr-2 lucide lucide-rotate-cw-icon lucide-rotate-cw">
                                <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                                <path d="M21 3v5h-5" />
                            </svg>
                            Intentar de Nuevo
                        </v-btn>

                        <v-btn v-if="isBlockMode" class="regular-btn-primary font-rubik ml-4 mt-2" depressed
                            @click="$emit('navigate-next')">

                            Ir a las flashcards

                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="ml-2 lucide lucide-arrow-right-icon lucide-arrow-right">
                                <path d="M5 12h14" />
                                <path d="m12 5 7 7-7 7" />
                            </svg>
                        </v-btn>

                        <v-btn v-if="false" class="action-btn regular-btn font-rubik ml-4" depressed
                            @click="isQuizFinished = true; reviewMode = false">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="mr-2 lucide lucide-arrow-left">
                                <path d="m12 19-7-7 7-7" />
                                <path d="M19 12H5" />
                            </svg>
                            Volver a Resultados
                        </v-btn>
                    </div>
                </template>

                <!-- Vista de resultados normal -->
                <template v-else>
                    <div class="text-center mb-6">
                        <div class="d-flex justify-center mb-5">
                            <div class="result-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round" class="lucide lucide-check">
                                    <path d="M20 6 9 17l-5-5" />
                                </svg>
                            </div>
                        </div>
                        <h1 class="font-rubik font-weight-bold mb-3" style="font-size: 2rem;">¡Has completado el
                            cuestionario!</h1>
                        <h2 class="font-rubik grey--text text--darken-1" style="font-size: 1.5rem;">Has acertado {{
                            correctAnswers }} / {{ allQuestions.length }} ({{ scorePercentage }}%)</h2>
                    </div>

                    <v-card class="key-improvement-area mb-8" flat outlined>
                        <v-card-title class="justify-center font-weight-bold">Áreas principales de mejora</v-card-title>

                        <v-card-text class="text-center">
                            <p class="grey--text text--darken-1 mb-4">
                                Actualiza a un plan de pago para obtener retroalimentación personalizada sobre tus
                                resultados del cuestionario.
                            </p>

                            <v-btn color="primary" class="premium-btn font-rubik px-6" elevation="0">
                                Prueba Gratuita (6 días)
                            </v-btn>
                        </v-card-text>
                    </v-card>

                    <div class="d-flex justify-center action-buttons flex-wrap">
                        <v-btn class="mr-4 action-btn regular-btn font-rubik" depressed @click="reviewAnswers">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round"
                                class="mr-2 lucide lucide-book-open-check-icon lucide-book-open-check">
                                <path d="M12 21V7" />
                                <path d="m16 12 2 2 4-4" />
                                <path
                                    d="M22 6V4a1 1 0 0 0-1-1h-5a4 4 0 0 0-4 4 4 4 0 0 0-4-4H3a1 1 0 0 0-1 1v13a1 1 0 0 0 1 1h6a3 3 0 0 1 3 3 3 3 0 0 1 3-3h6a1 1 0 0 0 1-1v-1.3" />
                            </svg>

                            Revisar respuestas
                        </v-btn>

                        <v-btn class="action-btn regular-btn mx-2 font-rubik" depressed @click="goHome">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="mr-2 lucide lucide-house-icon lucide-house">
                                <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" />
                                <path
                                    d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                            </svg>

                            Ir a Inicio
                        </v-btn>

                        <v-btn class="ml-4 action-btn regular-btn font-rubik" depressed @click="retryQuiz">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="mr-2 lucide lucide-rotate-cw-icon lucide-rotate-cw">
                                <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                                <path d="M21 3v5h-5" />
                            </svg>
                            Reiniciar Cuestionario
                        </v-btn>
                    </div>
                </template>
            </div>

            <!-- Contenedor principal para scroll + paginación -->
            <template v-else>

                <div v-if="progressPercentage > 0" class="progress-bar"
                    :style="{ position: 'fixed', top: '60px', left: '0', right: '0', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center', width: `calc(100vw - ${navWidth + 15}px)` }">
                    <v-progress-linear :value="progressPercentage" color="primary" height="8"></v-progress-linear>

                </div>

                <!-- Área de contenido scrollable -->
                <div class="scrollable-quiz-content pt-12 pb-6" ref="scrollableContent">
                    <v-card class="question-card rounded-0 mb-4 no-background-hover" elevation="2" width="100%" rounded
                        style="max-width: 800px; margin-left: auto; margin-right: auto;" :ripple="false">

                        <v-card-text class="pa-0 pb-1"> <!-- Adjusted padding-bottom -->
                            <h2 class="mb-10 question-title" v-html="currentQuestion.question"></h2>

                            <!-- Pregunta de opción múltiple -->
                            <template v-if="currentQuestion.type === 'multiple_choice'">
                                <div class="option-cards-container">
                                    <v-row>
                                        <v-col cols="12" md="12" v-for="(option, optIndex) in currentQuestion.options"
                                            :key="optIndex">
                                            <v-card :class="[
                                                'option-card',
                                                {
                                                    'option-selected': selectedAnswer === option && !isAnswered,
                                                    'option-correct': isAnswered && option === currentQuestion.answer,
                                                    'option-incorrect': isAnswered && selectedAnswer === option && option !== currentQuestion.answer,
                                                    'option-keyboard-selected': selectedOptionIndex === optIndex && !isAnswered && showKeyboard
                                                }
                                            ]" :ripple="false" flat :outlined="true"
                                                @click="!isAnswered && selectOption(option)"
                                                :ref="`option-${optIndex}`">
                                                <div class="d-flex align-center pa-4">
                                                    <div class="option-number mr-3">{{ optIndex + 1 }}</div>
                                                    <div class="option-text">{{ option }}</div>
                                                </div>
                                            </v-card>
                                        </v-col>
                                    </v-row>
                                </div>
                            </template>

                            <!-- Pregunta de verdadero/falso -->
                            <template v-else-if="currentQuestion.type === 'true_false'">
                                <div class="option-cards-container">
                                    <v-row>
                                        <v-col cols="12" md="12" v-for="(option, optIndex) in getOptionsArray()"
                                            :key="optIndex">
                                            <v-card :class="[
                                                'option-card',
                                                {
                                                    'option-selected': selectedAnswer === option && !isAnswered,
                                                    'option-correct': isAnswered && option === currentQuestion.answer,
                                                    'option-incorrect': isAnswered && selectedAnswer === option && option !== currentQuestion.answer,
                                                    'option-keyboard-selected': selectedOptionIndex === optIndex && !isAnswered && showKeyboard
                                                }
                                            ]" :ripple="false" flat :outlined="true"
                                                @click="!isAnswered && selectOption(option)"
                                                :ref="`option-${optIndex}`">
                                                <div class="d-flex align-center pa-4">
                                                    <div class="option-number mr-3">{{ optIndex + 1 }}</div>
                                                    <div class="option-text">{{ option }}</div>
                                                </div>
                                            </v-card>
                                        </v-col>
                                    </v-row>
                                </div>
                            </template>

                            <!-- Pregunta de rellenar espacios -->
                            <div v-else-if="currentQuestion.type === 'fill_in_the_blank'"
                                class="justify-end d-flex flex-column">
                                <v-text-field v-model="selectedAnswer" label="Tu respuesta" :disabled="isAnswered"
                                    @keydown.enter="checkAnswer" outlined @keyup.enter.prevent autofocus
                                    ref="fillBlankInput"></v-text-field>
                                <div v-if="!isAnswered" class="d-flex justify-end">
                                    <v-btn color="primary" max-width="190px" class="mt-3 primary-btn"
                                        @click="checkAnswer">
                                        Responder
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                                            stroke-linecap="round" stroke-linejoin="round"
                                            class="ml-1 lucide lucide-check-icon lucide-check">
                                            <path d="M20 6 9 17l-5-5" />
                                        </svg>
                                    </v-btn>
                                </div>
                            </div>

                            <!-- Pregunta de respuesta corta -->
                            <div v-else-if="currentQuestion.type === 'short_answer'"
                                class="justify-end d-flex flex-column">
                                <v-text-field v-model="selectedAnswer" label="Tu respuesta" :disabled="isAnswered"
                                    outlined auto-grow @keydown.enter.prevent @keydown.enter="checkAnswer"
                                    @keyup.enter.prevent autofocus ref="shortAnswerInput"></v-text-field>
                                <div v-if="!isAnswered" class="d-flex justify-end">
                                    <v-btn color="primary" max-width="190px" class="mt-3 primary-btn"
                                        @click="checkAnswer">
                                        Responder
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                                            stroke-linecap="round" stroke-linejoin="round"
                                            class="ml-1 lucide lucide-check-icon lucide-check">
                                            <path d="M20 6 9 17l-5-5" />
                                        </svg>
                                    </v-btn>
                                </div>
                            </div>

                            <!-- Preguntas de emparejamiento (matching_pairs) -->
                            <template v-else-if="currentQuestion.type === 'matching_pairs'">
                                <div class="matching-container">
                                    <div class="matching-instructions mb-4">
                                        Arrastra los términos de la izquierda a las definiciones correctas de la derecha
                                        o haz clic en ambos para emparejar
                                    </div>

                                    <div class="matching-pairs-container">
                                        <v-row class="matching-row">
                                            <v-col cols="5" class="matching-column left-column">
                                                <div v-for="(term, index) in currentQuestion.terms"
                                                    :key="`term-${index}`" class="matching-item term-item" :class="{
                                                        'matched': matchedItems[index] !== undefined,
                                                        'disabled': isAnswered,
                                                        'active': activeTerm === index
                                                    }" @mousedown="!isAnswered && startDrag(index, 'term')"
                                                    @touchstart="!isAnswered && startDrag(index, 'term')"
                                                    @click="!isAnswered && handleTermClick(index)"
                                                    :ref="`term-${index}`">
                                                    <div class="matching-content">
                                                        <div class="term-indicator">{{ index + 1 }}</div>
                                                        <div class="term-text">{{ term }}</div>
                                                    </div>
                                                </div>
                                            </v-col>

                                            <v-col cols="2" class="matching-column middle-column">
                                                <div class="matching-lines-container">
                                                    <svg class="matching-lines-svg" ref="linesSvg">
                                                        <line v-for="(match, index) in userMatches"
                                                            :key="`line-${index}`" :x1="match.x1" :y1="match.y1"
                                                            :x2="match.x2" :y2="match.y2" class="matching-line"
                                                            :class="{ 'active-line': activeTerm !== null && matchedItems[activeTerm] === match.valueIndex }" />
                                                    </svg>
                                                    <!-- Línea activa durante drag -->
                                                    <svg class="matching-lines-svg"
                                                        v-if="dragActive || activeTerm !== null" ref="activeLine">
                                                        <line :x1="dragLineCoords.x1" :y1="dragLineCoords.y1"
                                                            :x2="dragLineCoords.x2" :y2="dragLineCoords.y2"
                                                            class="matching-line active-line" />
                                                    </svg>
                                                </div>
                                            </v-col>

                                            <v-col cols="5" class="matching-column right-column">
                                                <div v-for="(value, index) in currentQuestion.values"
                                                    :key="`value-${index}`" class="matching-item value-item" :class="{
                                                        'matched': Object.values(matchedItems).includes(index),
                                                        'disabled': isAnswered,
                                                        'active': activeValue === index,
                                                        'drop-target': (dragActive || activeTerm !== null) && termBeingDragged !== null && !Object.values(matchedItems).includes(index)
                                                    }" @mouseup="!isAnswered && endDrag(index)"
                                                    @touchend="!isAnswered && endDrag(index)"
                                                    @touchmove.prevent="!isAnswered && handleTouchMove($event)"
                                                    @mouseover="!isAnswered && handleValueHover(index)"
                                                    @click="!isAnswered && handleValueClick(index)"
                                                    :ref="`value-${index}`">
                                                    <div class="matching-content">
                                                        <div class="value-text">{{ value }}</div>
                                                        <div class="value-indicator">{{ String.fromCharCode(65 + index)
                                                        }}</div>
                                                    </div>
                                                </div>
                                            </v-col>
                                        </v-row>
                                    </div>

                                    <div v-if="!isAnswered && userMatches.length > 0" class="d-flex justify-end mt-4">
                                        <v-btn small text color="error" class="mr-2" @click="clearLastMatch">
                                            <v-icon small class="mr-1">mdi-undo</v-icon>
                                            Deshacer último
                                        </v-btn>
                                        <v-btn small text color="error" class="mr-4" @click="clearAllMatches">
                                            <v-icon small class="mr-1">mdi-delete-sweep</v-icon>
                                            Borrar todo
                                        </v-btn>
                                    </div>

                                    <div v-if="!isAnswered && Object.keys(matchedItems).length === currentQuestion.terms.length"
                                        class="d-flex justify-end mt-4">
                                        <v-btn color="primary" class="mt-3 primary-btn" @click="checkAnswer">
                                            Comprobar respuestas
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                                                stroke-linecap="round" stroke-linejoin="round"
                                                class="ml-1 lucide lucide-check-icon lucide-check">
                                                <path d="M20 6 9 17l-5-5" />
                                            </svg>
                                        </v-btn>
                                    </div>
                                </div>
                            </template>

                            <!-- Preguntas de ordenamiento (ordering) -->
                            <template v-else-if="currentQuestion.type === 'ordering'">
                                <div class="ordering-container">
                                    <div class="ordering-instructions mb-4">
                                        Arrastra los elementos para ordenarlos correctamente. Los números indican la
                                        posición ideal.
                                    </div>

                                    <v-card flat class="ordering-items-container">
                                        <div class="d-flex ordering-wrapper">
                                            <!-- Números fijos a la izquierda -->
                                            <div class="ordering-numbers">
                                                <div v-for="(_, index) in orderingItems" :key="`num-${index}`"
                                                    class="order-number-container mb-3">
                                                    <div class="order-number">{{ index + 1 }}</div>
                                                </div>
                                            </div>

                                            <!-- Elementos arrastrables -->
                                            <div class="ordering-items-draggable">
                                                <draggable v-model="orderingItems" :disabled="isAnswered"
                                                    class="ordering-list">
                                                    <div v-for="(item, index) in orderingItems" :key="`item-${index}`"
                                                        class="ordering-item-wrapper">
                                                        <v-card class="ordering-item flex-grow-1" :class="{
                                                            'correct-position': isAnswered && item.isCorrect,
                                                            'incorrect-position': isAnswered && !item.isCorrect
                                                        }">
                                                            <div class="d-flex ordering-item-content">
                                                                <div v-if="!isAnswered" class="drag-indicator mr-3">
                                                                    <v-icon small>mdi-drag</v-icon>
                                                                </div>
                                                                <div v-else class="position-indicator mr-3">
                                                                    <v-icon small v-if="item.isCorrect"
                                                                        color="success">mdi-check</v-icon>
                                                                    <v-icon small v-else
                                                                        color="error">mdi-close</v-icon>
                                                                </div>
                                                                <div class="item-text">{{ item.text }}</div>
                                                            </div>
                                                        </v-card>
                                                    </div>
                                                </draggable>
                                            </div>
                                        </div>
                                    </v-card>

                                    <div v-if="!isAnswered" class="d-flex justify-end mt-4">
                                        <v-btn color="primary" class="mt-3 primary-btn" @click="checkAnswer">
                                            Comprobar orden
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                                                stroke-linecap="round" stroke-linejoin="round"
                                                class="ml-1 lucide lucide-check-icon lucide-check">
                                                <path d="M20 6 9 17l-5-5" />
                                            </svg>
                                        </v-btn>
                                    </div>
                                </div>
                            </template>

                            <!-- Retroalimentación de respuesta -->
                            <v-expand-transition>
                                <div v-if="isAnswered" class="mt-8 answer-feedback d-flex align-center"
                                    :class="$vuetify.breakpoint.xs ? 'flex-column' : ''">
                                    <div :type="isCorrect ? 'success' : 'error'" dense text style="flex: 1;">
                                        <template v-if="isCorrect">
                                            <span class="text-success">
                                                ¡Correcto!
                                            </span>
                                            <br>
                                            <div v-if="settings.showExplanations && currentQuestion.explanation"
                                                class="explanation-container" ref="explanationContainer"
                                                :id="'explanation-' + currentIndex">
                                                <span class="font-weight-bold">Explicación:</span>
                                                <span
                                                    :class="{ 'blurred-text': shouldBlurExplanation() && !userIsPremium }"
                                                    style="font-size: 1rem; color: #858487">
                                                    {{ currentQuestion.explanation || '' }}
                                                </span>
                                                <div v-if="shouldBlurExplanation() && !userIsPremium"
                                                    class="premium-explanation-overlay">
                                                    <v-icon small color="grey darken-1"
                                                        class="mr-1">mdi-lock-outline</v-icon>
                                                    <span>Explicación disponible con Premium.</span>
                                                    <v-btn small color="primary" class="ml-2 upgrade-btn-blur"
                                                        @click="goToPremium">Ver más</v-btn>
                                                </div>
                                            </div>
                                        </template>
                                        <template v-else>
                                            <span class="text-error">
                                                Incorrecto
                                            </span>
                                            <br>
                                            <span style="font-size: 1rem; color: #858487">
                                                La respuesta correcta es:
                                                <span v-if="currentQuestion.type === 'ordering'"
                                                    style="font-weight: 500; font-family: -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Roboto, Ubuntu, sans-serif; color: #3C3C3C; display: block; margin-top: 8px;">
                                                    <div v-for="(item, index) in getCorrectOrderingItems()" :key="index"
                                                        class="d-flex align-center mt-2 correct-order-item">
                                                        <div class="order-number-correct mr-3">{{ index + 1 }}</div>
                                                        <div>{{ item.text }}</div>
                                                    </div>
                                                </span>
                                                <span v-else-if="currentQuestion.type === 'matching_pairs'"
                                                    style="font-weight: 500; font-family: -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Roboto, Ubuntu, sans-serif; color: #3C3C3C; display: block; margin-top: 8px;">
                                                    <div v-for="(term, index) in currentQuestion.terms" :key="index"
                                                        class="d-flex align-center mt-2 correct-match-item">
                                                        <div class="match-item-term">
                                                            <span class="match-indicator mr-2">{{ index + 1 }}</span>
                                                            {{ term }}
                                                        </div>
                                                        <div class="match-arrow mx-2">→</div>
                                                        <div class="match-item-value">
                                                            {{
                                                                currentQuestion.values[currentQuestion.correctMatches[index]]
                                                            }}
                                                            <span class="match-indicator ml-2">{{ String.fromCharCode(65
                                                                + currentQuestion.correctMatches[index]) }}</span>
                                                        </div>
                                                    </div>
                                                </span>
                                                <span v-else-if="currentQuestion.type === 'true_false'"
                                                    style="font-weight: 500; font-family: -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Roboto, Ubuntu, sans-serif; color: #3C3C3C;">
                                                    {{ currentQuestion.answer }}
                                                </span>
                                                <span v-else
                                                    style="font-weight: 500; font-family: -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Roboto, Ubuntu, sans-serif; color: #3C3C3C;">
                                                    {{ currentQuestion.answer }}
                                                </span>
                                            </span>
                                            <div v-if="settings.showExplanations && currentQuestion.explanation"
                                                class="explanation-container mt-1" ref="explanationContainer"
                                                :id="'explanation-' + currentIndex">
                                                <span class="font-weight-bold">Explicación:</span>
                                                <span
                                                    :class="{ 'blurred-text': shouldBlurExplanation() && !userIsPremium }">
                                                    {{ currentQuestion.explanation }}
                                                </span>
                                                <div v-if="shouldBlurExplanation() && !userIsPremium"
                                                    class="premium-explanation-overlay">
                                                    <v-icon small color="grey darken-1"
                                                        class="mr-1">mdi-lock-outline</v-icon>
                                                    <span>Explicación disponible con Premium.</span>
                                                    <v-btn small color="primary" class="ml-2 upgrade-btn-blur"
                                                        @click="goToPremium">Ver más</v-btn>
                                                </div>
                                            </div>
                                        </template>

                                        <div v-if="false" class="d-flex justify-center mt-10">
                                            <v-btn small color="primary-btn-next" @click="nextQuestion"
                                                class="pl-5 pr-4 mx-auto mt-10">
                                                {{ isBlockMode ? 'Siguiente' : 'Siguiente pregunta' }}
                                                <!-- Ir a flashcards -->

                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                                    viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"
                                                    stroke-linecap="round" stroke-linejoin="round"
                                                    class="lucide ml-2 lucide-arrow-right-icon lucide-arrow-right">
                                                    <path d="M5 12h14" />
                                                    <path d="m12 5 7 7-7 7" />
                                                </svg>
                                            </v-btn>
                                        </div>
                                    </div>

                                    <!-- Botón para ver resultados si todas las preguntas han sido respondidas -->
                                    <template v-if="allQuestionsAnswered">
                                        <v-btn small color="primary-btn" @click="showResults"
                                            class="pl-4 pr-5 font-rubik results-btn">

                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                                                stroke-linecap="round" stroke-linejoin="round"
                                                class="mr-2 lucide lucide-trophy-icon lucide-trophy">
                                                <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
                                                <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
                                                <path d="M4 22h16" />
                                                <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
                                                <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
                                                <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
                                            </svg>

                                            Ver Resultados
                                        </v-btn>
                                    </template>

                                    <!-- Botón de siguiente solo para preguntas que aún no se han completado todas -->
                                    <template v-else>

                                    </template>
                                </div>
                            </v-expand-transition>
                        </v-card-text>
                    </v-card>

                    <!-- Botón de Ver Resultados centrado cuando todas las preguntas están respondidas (si no hay feedback visible) -->
                    <div v-if="allQuestionsAnswered && !isAnswered" class="d-flex justify-center mt-6 mb-4">
                        <v-btn color="primary" class="pl-4 pr-5 font-rubik results-btn" @click="showResults">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="mr-2 lucide lucide-trophy-icon lucide-trophy">
                                <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
                                <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
                                <path d="M4 22h16" />
                                <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
                                <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
                                <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
                            </svg>
                            Ver Resultados
                        </v-btn>
                    </div>
                </div>


                <div v-if="containerWidth < 850 && isAnswered" class="d-flex align-center justify-space-between mb-6"
                style="gap: 10px; max-width: 800px; margin: 0 auto; width: 100%;"
                >

                    <v-btn small
                    :style="$vuetify.breakpoint.xs ? 'flex: 1;' : ''"
                    color="regular-btn-previous" @click="previousQuestion" class="pl-4 pr-5">

                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                            stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                            class="lucide mr-2 lucide-arrow-right-icon lucide-arrow-right">
                            <path d="M19 12H5" />
                            <path d="M12 19 5 12 12 5" />
                        </svg>

                        {{ isBlockMode ? 'Anterior' : $vuetify.breakpoint.xs ? 'Anterior' : 'Anterior pregunta' }}
                        <!-- Ver explicación -->


                    </v-btn>
                    <v-spacer v-if="!$vuetify.breakpoint.xs"></v-spacer>

                    <v-btn 
                    :style="$vuetify.breakpoint.xs ? 'flex: 1;' : ''"
                    small color="primary-btn-next" @click="nextQuestion" class="pl-5 pr-4">
                        {{ isBlockMode ? 'Siguiente' : $vuetify.breakpoint.xs ? 'Siguiente' : 'Siguiente pregunta' }}
                        <!-- Ir a flashcards -->

                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                            stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                            class="lucide ml-2 lucide-arrow-right-icon lucide-arrow-right">
                            <path d="M5 12h14" />
                            <path d="m12 5 7 7-7 7" />
                        </svg>
                    </v-btn>
                </div>


                <!-- Paginación fija abajo -->
                <div class="quiz-pagination-container d-flex justify-space-between align-center">

                    <v-btn v-if="containerWidth > 850" small color="regular-btn-previous"
                        :class="$vuetify.breakpoint.xs ? 'mt-6' : ''" @click="previousQuestion" class="pl-4 pr-5">

                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                            stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                            class="lucide mr-2 lucide-arrow-right-icon lucide-arrow-right">
                            <path d="M19 12H5" />
                            <path d="M12 19 5 12 12 5" />
                        </svg>

                        {{ isBlockMode ? 'Anterior' : $vuetify.breakpoint.xs ? 'Anterior' : 'Anterior pregunta' }}


                    </v-btn>



                    <div style="flex: 1;" class="d-flex align-center justify-start">
                        <v-card flat class="pa-0 pb-0 rounded-lg" width="100%" color="transparent">
                            <div class="d-flex align-center grey lighten-4 px-3 py-2"
                                style="max-width: 450px; border-radius: 10px;"
                                :class="$vuetify.breakpoint.xs ? '' : 'mx-auto'">

                                <v-menu offset-y top content-class="rounded-lg flat no-shadow question-menu elevation-0"
                                    nudge-top="30" class="elevation-0" nudge-left="18" :close-on-content-click="true"
                                    :max-width="400" right>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn v-bind="attrs" v-on="on" fab color="transparent" small
                                            @click="toggleQuestionView">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                                                stroke-linecap="round" stroke-linejoin="round"
                                                class="lucide lucide-layout-grid-icon lucide-layout-grid">
                                                <rect width="7" height="7" x="3" y="3" rx="1" />
                                                <rect width="7" height="7" x="14" y="3" rx="1" />
                                                <rect width="7" height="7" x="14" y="14" rx="1" />
                                                <rect width="7" height="7" x="3" y="14" rx="1" />
                                            </svg>
                                        </v-btn>
                                    </template>
                                    <div class="d-flex flex-wrap justify-center pa-4 mt-2"
                                        style="border: 2px solid #E5E5E5; border-radius: 10px;">
                                        <div v-for="(_, index) in allQuestions" :key="index" :class="[
                                            'page-number',
                                            {
                                                'active': currentIndex === index,
                                                'not-answered': currentIndex !== index && questionResults[index] === undefined,
                                                'completed': questionResults[index] === true,
                                                'error': questionResults[index] === false
                                            }
                                        ]" @click="goToQuestion(index)">
                                            {{ index + 1 }}
                                        </div>
                                    </div>
                                </v-menu>

                                <v-btn icon small class="mx-1" @click="confirmRetry" title="Reiniciar cuestionario">
                                    <v-icon color="#1E1E1E">mdi-refresh</v-icon>
                                </v-btn>

                                <div class="d-flex align-center mx-8" style="flex: 1; min-width: 200px;">
                                    <span class="font-weight-bold">{{ currentIndex + 1 }}</span>

                                    <!-- Progreso del cuestionario -->
                                    <v-progress-linear :value="progressPercentage" style="flex: 1;" color="primary"
                                        height="7" class="flex-grow- mx-6 rounded-lg progress-bar"></v-progress-linear>

                                    <span class="font-weight-bold">{{ allQuestions.length }}</span>
                                </div>

                                <div v-if="!$vuetify.breakpoint.xs">
                                    <v-btn icon small class="mx-1" @click="previousQuestion"
                                        :disabled="isBlockMode ? false : currentIndex === 0">
                                        <v-icon color="#1E1E1E">mdi-chevron-left</v-icon>
                                    </v-btn>
                                    <v-btn icon small class="mx-1" @click="nextQuestion"
                                        :disabled="isBlockMode ? false : currentIndex === allQuestions.length - 1">
                                        <v-icon color="#1E1E1E">mdi-chevron-right</v-icon>
                                    </v-btn>
                                </div>
                            </div>
                        </v-card>
                    </div>

                    <v-btn v-if="containerWidth > 850" small color="primary-btn-next"
                        :class="$vuetify.breakpoint.xs ? 'mt-6' : ''" @click="nextQuestion" class="pl-5 pr-4">
                        {{ isBlockMode ? 'Siguiente' : $vuetify.breakpoint.xs ? 'Siguiente' : 'Siguiente pregunta' }}

                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                            stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                            class="lucide ml-2 lucide-arrow-right-icon lucide-arrow-right">
                            <path d="M5 12h14" />
                            <path d="m12 5 7 7-7 7" />
                        </svg>
                    </v-btn>
                </div>
            </template>

            <!-- Diálogos -->
            <v-dialog v-model="showRetryDialog" max-width="400">
                <v-card class="rounded-lg">
                    <v-card-title class="text-h5 font-rubik">
                        ¿Reiniciar cuestionario?
                    </v-card-title>
                    <v-card-text class="text-body-1">
                        Se borrarán todas tus respuestas actuales. ¿Estás seguro de que quieres reiniciar?
                    </v-card-text>
                    <v-card-actions class="pa-4">
                        <v-spacer></v-spacer>
                        <v-btn text @click="showRetryDialog = false" class="mr-2">
                            Cancelar
                        </v-btn>
                        <v-btn color="primary" @click="retryQuiz" class="font-rubik">
                            Reiniciar
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
            <v-dialog v-model="showDiscardDialog" max-width="400">
                <v-card class="rounded-lg">
                    <v-card-title class="text-h5 font-rubik">
                        ¿Descartar progreso guardado?
                    </v-card-title>
                    <v-card-text class="text-body-1">
                        Se eliminará el progreso guardado del cuestionario. ¿Estás seguro?
                    </v-card-text>
                    <v-card-actions class="pa-4">
                        <v-spacer></v-spacer>
                        <v-btn text @click="showDiscardDialog = false" class="mr-2">
                            Cancelar
                        </v-btn>
                        <v-btn color="error" @click="discardProgress" class="font-rubik">
                            Descartar
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </template>


        <!-- Dialog para funcionalidad premium -->
        <features-dialog :showDialog.sync="showFeaturesDialog" />
    </div>
</template>

<script>
// Importar draggable para las preguntas de ordenamiento
import draggable from 'vuedraggable'
import FeaturesDialog from '../../components/premium/FeaturesDialog.vue';

export default {
    name: 'QuizSingleQuestion',
    components: {
        draggable,
        FeaturesDialog
    },
    props: {
        quiz: {
            type: Object,
            required: true,
            default: () => ({})
        },
        settings: {
            type: Object,
            default: () => ({
                shuffleQuestions: false,
                showExplanations: true,
                difficultyFilter: 'all'
            })
        },
        showResultsParam: {
            type: Boolean,
            default: false
        },
        quizResultData: {
            type: Object,
            default: () => ({})
        },
        documentId: { // <--- Prop para identificar el documento
            type: [String, Number],
            required: true
        },
        blockUniqueId: { // <--- Prop para identificar el bloque
            type: String,
            required: true // Asegurarse que siempre se pase
        },
        isBlockMode: {
            type: Boolean,
            default: false
        },
        navWidth: {
            type: Number,
            default: 0
        }
    },
    data() {
        return {
            currentIndex: 0,
            allQuestions: [],
            originalQuestionsOrder: [], // Para guardar el orden original de las preguntas
            selectedAnswer: null,
            showFeaturesDialog: false,
            isAnswered: false,
            isCorrect: false,
            questionResults: {}, // Para almacenar resultados por índice
            userAnswers: {}, // Para almacenar las respuestas específicas por índice
            isQuizFinished: false,
            isSaving: false,
            isResultSaved: false,
            selectedOptionIndex: null, // Índice para navegación por teclado
            showKeyboard: false,
            reviewMode: false, // Nuevo estado para modo de revisión
            expandedQuestions: {}, // Para controlar qué preguntas están expandidas en la revisión
            showRetryDialog: false, // Nuevo estado para el diálogo
            quizId: '', // Para identificar el cuestionario en localStorage
            showDiscardDialog: false, // Para el diálogo de descartar progreso
            hasStoredProgress: false, // Para mostrar el botón de descartar progreso
            savedProgressData: null, // Almacenar temporalmente los datos cargados
            loadingQuiz: true, // Para indicar que el cuestionario está cargando

            // Para preguntas de emparejamiento
            matchedItems: {}, // Objeto para almacenar las coincidencias {termIndex: valueIndex}
            userMatches: [], // Array para almacenar las líneas de emparejamiento
            activeTerm: null, // Término actualmente seleccionado/arrastrado
            activeValue: null, // Valor actualmente seleccionado/en hover
            dragActive: false, // Si hay un arrastre activo
            termBeingDragged: null, // El término que se está arrastrando
            dragLineCoords: { x1: 0, y1: 0, x2: 0, y2: 0 }, // Coordenadas de la línea de arrastre

            // Para preguntas de ordenamiento
            orderingItems: [], // Array para almacenar los elementos ordenables
        };
    },
    computed: {
        userIsPremium() {
            return this.$root.currentUser && this.$root.currentUser.premium;
        },
        currentQuestion() {
            return this.allQuestions[this.currentIndex] || {};
        },
        progressPercentage() {
            return Math.round(((this.currentIndex + 1) / Math.max(this.allQuestions.length, 1)) * 100);
        },
        completedQuestions() {
            return Object.keys(this.questionResults).length;
        },
        correctAnswers() {
            return Object.values(this.questionResults).filter(result => result).length;
        },
        scorePercentage() {
            if (this.completedQuestions === 0) return 0;
            return Math.round((this.correctAnswers / this.completedQuestions) * 100);
        },
        formatFillBlankQuestion() {
            if (!this.currentQuestion.question) return '';

            // Para las preguntas de rellenar espacios, mostramos el espacio en blanco
            if (this.currentQuestion.type === 'fill_in_the_blank') {
                if (this.isAnswered) {
                    // Si ya se respondió, mostramos la respuesta
                    return this.currentQuestion.question.replace('________', `<strong>${this.selectedAnswer}</strong>`);
                } else {
                    // Si no se ha respondido, mostramos el espacio en blanco
                    return this.currentQuestion.question.replace('________', '<u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</u>');
                }
            }

            return this.currentQuestion.question;
        },
        isLastQuestion() {
            return this.currentIndex === this.allQuestions.length - 1;
        },
        allQuestionsAnswered() {
            return this.completedQuestions === this.allQuestions.length && this.allQuestions.length > 0;
        },
        localStorageKey() {
            // Utiliza el blockUniqueId y documentId para la clave de localStorage
            if (!this.documentId || !this.blockUniqueId) {
                console.warn('DocumentId o BlockUniqueId no están disponibles para generar la clave de localStorage.');
                // Fallback a una clave genérica si blockUniqueId no está disponible (aunque debería estarlo)
                return `quiz_progress_fallback_${Date.now()}`;
            }
            return `quiz_progress_doc_${this.documentId}_block_${this.blockUniqueId}`;
        },
        containerWidth() {
            return window.innerWidth - this.navWidth;
        }
    },
    watch: {
        quiz: {
            immediate: true,
            handler() {
                // Establecer un ID único para este cuestionario
                this.generateQuizId();

                // Verificar si existe progreso guardado
                this.checkSavedProgress();
            }
        },
        // Guardar el progreso cada vez que cambie algo importante
        questionResults: {
            deep: true,
            handler() {
                this.saveProgress();
            }
        },
        userAnswers: {
            deep: true,
            handler() {
                this.saveProgress();
            }
        },
        currentIndex: {
            handler() {
                this.saveProgress();
            }
        },
        showResultsParam: {
            handler(newVal) {
                if (newVal) {
                    this.reviewMode = true; // <--- Mostrar revisión directamente
                    this.isQuizFinished = true; // <--- Marcar como finalizado
                    if (this.quizResultData && Object.keys(this.quizResultData).length > 0) {
                        this.loadQuizResults(); // Cargar datos si existen
                    }
                }
            }
        },
        completedQuestions: {
            handler(newValue) {
                // Emitir solo cuando todas las preguntas estén completadas
                if (this.allQuestions.length > 0 && newValue === this.allQuestions.length) {
                    this.$emit('quiz-completed', {
                        score: this.scorePercentage,
                        totalQuestions: this.allQuestions.length,
                        correctAnswers: this.correctAnswers,
                        answers: this.userAnswers, // Enviar las respuestas del usuario
                        results: this.questionResults // Enviar los resultados (true/false)
                    });
                }
            }
        },
        showFeaturesDialog(isShown) {
            if (!isShown && this.$route.query.premium_from) {
                const query = { ...this.$route.query };
                delete query.premium_from;
                this.$router.push({ query }).catch(err => {
                    if (err.name !== 'NavigationDuplicated') {
                        console.error(err);
                    }
                });
            }
        },
    },
    methods: {
        formatTimestamp(timestamp) {
            if (!timestamp) return '';

            const now = new Date();
            const savedDate = new Date(timestamp);
            const diffMs = now - savedDate;
            const diffMins = Math.floor(diffMs / (1000 * 60));
            const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

            if (diffMins < 1) {
                return 'Hace unos segundos';
            } else if (diffMins < 60) {
                return `Hace ${diffMins} ${diffMins === 1 ? 'minuto' : 'minutos'}`;
            } else if (diffHours < 24) {
                return `Hace ${diffHours} ${diffHours === 1 ? 'hora' : 'horas'}`;
            } else if (diffDays < 7) {
                return `Hace ${diffDays} ${diffDays === 1 ? 'día' : 'días'}`;
            } else {
                // Formatear fecha completa para más de una semana
                const options = { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' };
                return savedDate.toLocaleDateString('es-ES', options);
            }
        },

        prepareQuestions(useStoredOrder = false) {
            this.allQuestions = [];
            this.loadingQuiz = true;

            // Convertir cada tipo de pregunta al formato unificado con su tipo
            if (this.quiz.multiple_choice && this.quiz.multiple_choice.length) {
                const formattedQuestions = this.quiz.multiple_choice.map(q => ({
                    ...q,
                    question: q.question,
                    options: q.options,
                    answer: q.answer || q.correct_answer,
                    type: 'multiple_choice',
                    level: q.level || 'medium'
                }));
                this.allQuestions = [...this.allQuestions, ...formattedQuestions];
            }

            if (this.quiz.true_false && this.quiz.true_false.length) {
                const formattedQuestions = this.quiz.true_false.map(q => ({
                    ...q,
                    question: q.question,
                    answer: q.answer,
                    options: q.options || { true: 'Verdadero', false: 'Falso' },
                    type: 'true_false',
                    level: q.level || 'medium'
                }));
                this.allQuestions = [...this.allQuestions, ...formattedQuestions];
            }

            if (this.quiz.fill_in_the_blank && this.quiz.fill_in_the_blank.length) {
                const formattedQuestions = this.quiz.fill_in_the_blank.map(q => ({
                    ...q,
                    question: q.question || q.statement,
                    answer: q.answer,
                    type: 'fill_in_the_blank',
                    level: q.level || 'medium'
                }));
                this.allQuestions = [...this.allQuestions, ...formattedQuestions];
            }

            if (this.quiz.short_answer && this.quiz.short_answer.length) {
                const formattedQuestions = this.quiz.short_answer.map(q => ({
                    ...q,
                    question: q.question,
                    answer: q.answer,
                    type: 'short_answer',
                    level: q.level || 'medium'
                }));
                this.allQuestions = [...this.allQuestions, ...formattedQuestions];
            }

            // Añadir preguntas de emparejamiento
            if (this.quiz.matching_pairs && this.quiz.matching_pairs.length) {
                const formattedQuestions = this.quiz.matching_pairs.map(q => {
                    // Extraer los términos y valores de los items
                    const terms = q.items.map(item => item.left);
                    const values = q.items.map(item => item.right);
                    // Crear un mapa de respuestas correctas
                    const correctMatches = {};
                    q.items.forEach((item, index) => {
                        correctMatches[index] = index; // En este caso, la posición coincide
                    });

                    return {
                        ...q,
                        question: q.instruction || 'Empareja los siguientes términos con sus definiciones',
                        terms: terms,
                        values: values,
                        correctMatches: correctMatches,
                        type: 'matching_pairs',
                        level: q.level || 'medium'
                    };
                });
                this.allQuestions = [...this.allQuestions, ...formattedQuestions];
            }

            // Añadir preguntas de ordenamiento
            if (this.quiz.ordering && this.quiz.ordering.length) {
                const formattedQuestions = this.quiz.ordering.map(q => {
                    return {
                        ...q,
                        question: q.question || q.instruction,
                        items: q.items.map((text, index) => ({
                            text,
                            originalIndex: q.correct_order ? q.correct_order[index] : index
                        })),
                        type: 'ordering',
                        level: q.level || 'medium'
                    };
                });
                this.allQuestions = [...this.allQuestions, ...formattedQuestions];
            }

            console.log('Preguntas preparadas:', this.allQuestions.length);

            // Si hay un orden guardado y se solicitó usarlo
            if (useStoredOrder && this.savedProgressData && this.savedProgressData.questionsOrder) {
                this.reorderQuestionsByStoredOrder(this.savedProgressData.questionsOrder);
            } else {
                // Guardar el orden original antes de mezclar
                this.originalQuestionsOrder = this.allQuestions.map(q => q.question);

                // Mezclar las preguntas solo si está configurado para hacerlo
                if (this.settings.shuffleQuestions) {
                    this.shuffleQuestions();
                }
            }

            // Reiniciar el estado
            this.resetQuiz();
            this.loadingQuiz = false;

            // Si se debe mostrar resultados directamente
            if (this.showResultsParam && this.quizResultData && Object.keys(this.quizResultData).length > 0) {
                this.loadQuizResults();
            }
        },

        shuffleQuestions() {
            // Algoritmo de Fisher-Yates para mezclar el array
            for (let i = this.allQuestions.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [this.allQuestions[i], this.allQuestions[j]] = [this.allQuestions[j], this.allQuestions[i]];
            }

            // Actualizar el array de orden original después de mezclar
            this.originalQuestionsOrder = this.allQuestions.map(q => q.question);
        },

        reorderQuestionsByStoredOrder(storedOrder) {
            // Reordenar las preguntas según el orden guardado
            if (!storedOrder || storedOrder.length === 0) return;

            // Crear una copia de las preguntas actuales
            const currentQuestions = [...this.allQuestions];
            const reorderedQuestions = [];

            // Intentar reconstruir el mismo orden
            for (const questionText of storedOrder) {
                const index = currentQuestions.findIndex(q => q.question === questionText);
                if (index !== -1) {
                    reorderedQuestions.push(currentQuestions[index]);
                    currentQuestions.splice(index, 1); // Eliminar la pregunta ya utilizada
                }
            }

            // Añadir las preguntas que no estaban en el orden guardado (si hay nuevas)
            this.allQuestions = [...reorderedQuestions, ...currentQuestions];

            // Actualizar el orden original
            this.originalQuestionsOrder = this.allQuestions.map(q => q.question);
        },

        checkSavedProgress() {
            // Verificar si existe progreso guardado
            try {
                const savedProgress = localStorage.getItem(this.localStorageKey);

                if (!this.$route.query.show_results && savedProgress) {
                    const progressData = JSON.parse(savedProgress);

                    // Verificar si los datos no son demasiado antiguos (7 días)
                    const now = new Date().getTime();
                    const savedTime = progressData.timestamp || 0;
                    const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 días en milisegundos

                    if (now - savedTime > maxAge) {
                        // Los datos son demasiado antiguos, eliminarlos
                        localStorage.removeItem(this.localStorageKey);
                        this.hasStoredProgress = false;
                        this.prepareQuestions(false); // Preparar con orden aleatorio
                        return;
                    }

                    // Guardar los datos temporalmente
                    this.savedProgressData = progressData;
                    this.hasStoredProgress = true;

                    // --- CAMBIO PRINCIPAL ---
                    // Cargar directamente el progreso guardado sin preguntar
                    console.log('Progreso guardado encontrado, cargando automáticamente...');
                    this.prepareQuestions(true); // Preparar con orden guardado
                    this.loadSavedProgress();    // Cargar estado guardado
                    // --- FIN CAMBIO PRINCIPAL ---

                } else {
                    // No hay progreso guardado, preparar normalmente
                    this.hasStoredProgress = false;
                    this.prepareQuestions(false);
                }
            } catch (e) {
                console.error('Error al verificar el progreso guardado:', e);
                this.hasStoredProgress = false;
                this.prepareQuestions(false);
            } finally {
                this.loadingQuiz = false;
            }
        },

        loadSavedProgress() {
            // Usar los datos ya cargados en savedProgressData
            if (!this.savedProgressData) {
                console.error('No hay datos guardados para cargar');
                return;
            }

            try {
                const progressData = this.savedProgressData;

                // Restaurar el estado
                this.currentIndex = progressData.currentIndex || 0;

                // Restaurar resultados y respuestas de usuario
                if (progressData.questionResults) {
                    Object.keys(progressData.questionResults).forEach(index => {
                        this.$set(this.questionResults, index, progressData.questionResults[index]);
                    });
                }

                if (progressData.userAnswers) {
                    Object.keys(progressData.userAnswers).forEach(index => {
                        this.$set(this.userAnswers, index, progressData.userAnswers[index]);
                    });
                }

                console.log('Progreso cargado:', this.localStorageKey);

                // Forzar la actualización de la pregunta actual tras cargar el progreso
                this.$nextTick(() => {
                    // Asegurarse de que se muestra la respuesta correcta en la pregunta actual
                    if (this.questionResults.hasOwnProperty(this.currentIndex)) {
                        // Forzar a actualizar el estado de respuesta en la pregunta actual
                        this.selectedAnswer = this.userAnswers[this.currentIndex];
                        this.isAnswered = true;
                        this.isCorrect = this.questionResults[this.currentIndex];

                        // Para preguntas de tipo ordering, necesitamos reconstruir los elementos
                        if (this.currentQuestion && this.currentQuestion.type === 'ordering') {
                            this.initOrderingItems();

                            // Si la pregunta ya fue respondida, reconstruir también los estados de corrección
                            if (this.selectedAnswer) {
                                try {
                                    const userOrder = JSON.parse(this.selectedAnswer);

                                    // Restaurar el estado de corrección de cada elemento
                                    this.orderingItems.forEach((item, index) => {
                                        const originalItem = this.currentQuestion.items.find(i => i.text === item.text);
                                        if (originalItem) {
                                            const isCorrectPosition = originalItem.originalIndex === index;
                                            this.$set(this.orderingItems[index], 'isCorrect', isCorrectPosition);
                                        }
                                    });
                                } catch (e) {
                                    console.error('Error al restaurar el estado de ordenamiento:', e);
                                }
                            }
                        }
                    } else {
                        // Si la pregunta actual no ha sido respondida, asegurarse de inicializar elementos de ordenamiento
                        if (this.currentQuestion && this.currentQuestion.type === 'ordering') {
                            this.initOrderingItems();
                        }
                    }
                });

                // Verificar si todas las preguntas están respondidas
                this.checkIfQuizIsComplete();

                // Limpiar los datos temporales
                this.savedProgressData = null;
            } catch (e) {
                console.error('Error al cargar el progreso guardado:', e);
                this.hasStoredProgress = false;
            }
        },


        continueSavedProgress() {
            // El usuario eligió continuar con el progreso guardado
            this.showContinueProgressDialog = false;

            // Preparar las preguntas con el orden guardado
            this.prepareQuestions(true);

            // Restaurar el estado del progreso
            this.loadSavedProgress();
        },

        startNewQuiz() {
            // El usuario eligió empezar de nuevo
            this.showContinueProgressDialog = false;

            // Eliminar el progreso guardado
            try {
                localStorage.removeItem(this.localStorageKey);
            } catch (e) {
                console.error('Error al eliminar el progreso guardado:', e);
            }

            // Preparar las preguntas con orden aleatorio
            this.prepareQuestions(false);
        },

        saveProgress() {
            // No guardar si el cuestionario está terminado o en modo revisión
            if (this.isQuizFinished || this.reviewMode) return;

            // Verificar si hay al menos una respuesta contestada
            if (Object.keys(this.questionResults).length === 0) return;

            // Guardar el progreso actual en localStorage
            const progressData = {
                currentIndex: this.currentIndex,
                questionResults: this.questionResults,
                userAnswers: this.userAnswers,
                questionsOrder: this.originalQuestionsOrder, // Guardar el orden de las preguntas
                timestamp: new Date().getTime()
            };

            try {
                localStorage.setItem(this.localStorageKey, JSON.stringify(progressData));
                this.hasStoredProgress = true;
                console.log('Progreso guardado:', this.localStorageKey);
            } catch (e) {
                console.error('Error al guardar el progreso:', e);
            }
        },

        checkIfQuizIsComplete() {
            // Verificar si todas las preguntas han sido respondidas
            if (this.allQuestions.length > 0 && this.completedQuestions === this.allQuestions.length) {
                // Todas las preguntas están respondidas, mostrar el diálogo para ver resultados
                // this.$nextTick(() => {
                //     this.$toast.success('Has respondido todas las preguntas. ¿Quieres ver los resultados?', {
                //         position: 'top-center',
                //         timeout: 5000,
                //         closeOnClick: true,
                //         pauseOnFocusLoss: true,
                //         pauseOnHover: true,
                //         draggable: true,
                //         draggablePercent: 0.6,
                //         showCloseButtonOnHover: false,
                //         hideProgressBar: true,
                //         closeButton: 'button',
                //         icon: true,
                //         rtl: false
                //     });
                // });
                // this.isSaving = false;
                this.isQuizFinished = true;

                this.reviewMode = true;

            }
        },

        retryQuiz() {
            this.showRetryDialog = false; // Cerrar el diálogo si está abierto
            // Reiniciar el cuestionario
            this.resetQuiz();
            this.isQuizFinished = false;
            this.reviewMode = false;
            this.isResultSaved = false;
            this.isAnswered = false;
            this.isCorrect = false;
            this.selectedAnswer = null;
            this.selectedOptionIndex = null;
            this.questionResults = {};
            this.currentIndex = 0;

            // Eliminar el progreso guardado cuando se reinicia el cuestionario
            try {
                localStorage.removeItem(this.localStorageKey);
                this.$emit('quiz-retry'); // <--- Emitir evento al padre
            } catch (e) {
                console.error('Error al eliminar el progreso guardado:', e);
            }
        },

        goHome() {
            // Navegar a la página principal
            this.$router.push('/app');
        },

        toggleQuestionDetails(index) {
            // Alternar la expansión de la pregunta en la revisión
            this.$set(this.expandedQuestions, index, !this.expandedQuestions[index]);
        },

        showResults() {
            // Marcar las preguntas sin responder como incorrectas
            this.allQuestions.forEach((_, index) => {
                if (!this.questionResults.hasOwnProperty(index)) {
                    this.$set(this.questionResults, index, false);
                }
            });

            // Guardar los resultados y mostrar la pantalla de resultados
            // this.saveQuizResults();

            // Eliminar el progreso guardado al mostrar los resultados
            try {
                // localStorage.removeItem(this.localStorageKey);
                this.reviewMode = true; // <--- Mostrar revisión directamente
            } catch (e) {
                console.error('Error al eliminar el progreso guardado:', e);
            }
        },

        generateQuizId() {
            // Intenta usar el ID del quiz si existe
            if (this.quiz.id) {
                this.quizId = this.quiz.id;
                return;
            }

            // Si no, usa el ID del documento si está disponible en la ruta
            if (this.$route && this.$route.params && this.$route.params.documentId) {
                this.quizId = this.$route.params.documentId;
                return;
            }

            // Como último recurso, genera un hash basado en la primera pregunta
            if (this.quiz.multiple_choice && this.quiz.multiple_choice.length > 0) {
                const firstQuestion = this.quiz.multiple_choice[0].question;
                this.quizId = this.hashCode(firstQuestion);
            } else if (this.quiz.true_false && this.quiz.true_false.length > 0) {
                const firstQuestion = this.quiz.true_false[0].question;
                this.quizId = this.hashCode(firstQuestion);
            }
        },

        hashCode(str) {
            // Función simple para generar un hash a partir de un string
            let hash = 0;
            for (let i = 0; i < str.length; i++) {
                const char = str.charCodeAt(i);
                hash = ((hash << 5) - hash) + char;
                hash = hash & hash; // Convertir a entero de 32 bits
            }
            return hash.toString();
        },

        confirmRetry() {
            this.showRetryDialog = true;
        },

        confirmDiscardProgress() {
            // Usar nuestro propio diálogo en lugar de this.$dialog
            this.showDiscardDialog = true;
        },

        discardProgress() {
            try {
                // Cerrar el diálogo
                this.showDiscardDialog = false;

                // Eliminar el progreso guardado
                localStorage.removeItem(this.localStorageKey);
                this.hasStoredProgress = false;

                // Reiniciar el cuestionario
                this.resetQuiz();

                // Notificar al usuario
                this.$toast.success('Progreso descartado correctamente.', {
                    position: 'top-center',
                    timeout: 3000,
                });

            } catch (e) {
                console.error('Error al descartar el progreso:', e);
                this.$toast.error('Error al descartar el progreso.', {
                    position: 'top-center',
                    timeout: 3000,
                });
            }
        },

        resetQuiz() {
            this.currentIndex = 0;
            this.questionResults = {};
            this.userAnswers = {}; // Reiniciar las respuestas específicas
            this.isQuizFinished = false;
            this.isResultSaved = false;
            this.resetCurrentQuestion();
        },

        resetCurrentQuestion() {
            this.selectedAnswer = null;
            this.isAnswered = false;
            this.isCorrect = false;
            if (!this.showKeyboard) {
                this.selectedOptionIndex = null; // Reiniciar el índice seleccionado
            }

            // Si ya respondimos esta pregunta, mostrar el resultado previo
            if (this.questionResults.hasOwnProperty(this.currentIndex)) {
                const prevAnswer = this.userAnswers[this.currentIndex];
                this.selectedAnswer = prevAnswer;
                this.isAnswered = true;
                this.isCorrect = this.questionResults[this.currentIndex];

                // Si la pregunta ya fue respondida, hacer scroll a la explicación en dispositivos móviles
                if (false && this.$vuetify.breakpoint.xs) {
                    this.$nextTick(() => {
                        this.scrollToExplanation();
                    });
                }
            }

            // En preguntas de opción múltiple o verdadero/falso, seleccionar la primera opción por defecto
            if (!this.selectedAnswer && !this.isAnswered) {
                if (this.currentQuestion.type === 'multiple_choice' && this.currentQuestion.options?.length) {
                    this.selectedOptionIndex = 0;
                } else if (this.currentQuestion.type === 'true_false') {
                    this.selectedOptionIndex = 0;
                    const options = this.getOptionsArray();
                    if (options.length > 0) {
                        this.selectedAnswer = options[0]; // Usar la primera opción por defecto
                    }
                }
            }

            // Para preguntas de verdadero/falso donde ya se respondió, actualizar el índice seleccionado
            if (this.currentQuestion.type === 'true_false' && this.isAnswered) {
                const options = this.getOptionsArray();
                this.selectedOptionIndex = options.findIndex(opt => opt === this.selectedAnswer);
                if (this.selectedOptionIndex === -1) this.selectedOptionIndex = 0;
            }

            // Para preguntas de emparejamiento
            if (this.currentQuestion.type === 'matching_pairs' && !this.isAnswered) {
                this.clearAllMatches();
            }

            // Para preguntas de ordenamiento, siempre inicializar elementos
            if (this.currentQuestion.type === 'ordering') {
                this.initOrderingItems();

                // Si la pregunta ya fue respondida, restaurar el estado de corrección
                if (this.isAnswered && this.selectedAnswer) {
                    try {
                        const userOrder = JSON.parse(this.selectedAnswer);

                        // Restaurar el estado de corrección de cada elemento
                        this.orderingItems.forEach((item, index) => {
                            const isCorrectPosition = item.originalIndex === index;
                            this.$set(this.orderingItems[index], 'isCorrect', isCorrectPosition);
                        });
                    } catch (e) {
                        console.error('Error al restaurar el estado de ordenamiento:', e);
                    }
                }
            }

            // Enfocar el contenedor principal para permitir navegación con teclado
            this.focusQuizContainer();

            // Enfocar el campo de entrada si la pregunta requiere escritura y no ha sido respondida
            this.$nextTick(() => {
                if (!this.isAnswered) {
                    if (this.currentQuestion.type === 'fill_in_the_blank' && this.$refs.fillBlankInput) {
                        this.$refs.fillBlankInput.focus();
                    } else if (this.currentQuestion.type === 'short_answer' && this.$refs.shortAnswerInput) {
                        this.$refs.shortAnswerInput.focus();
                    }
                }
            });
        },

        checkAnswer() {
            // if (!this.selectedAnswer) return;
            this.isAnswered = true;
            switch (this.currentQuestion.type) {
                case 'multiple_choice':
                    // Para preguntas de opción múltiple, la respuesta debe ser exactamente igual
                    this.isCorrect = this.selectedAnswer === this.currentQuestion.answer;
                    break;
                case 'true_false':
                    // Para preguntas de verdadero/falso, comparamos los strings directamente
                    this.isCorrect = this.selectedAnswer === this.currentQuestion.answer;
                    console.log("TF Check:", {
                        userAnswer: this.selectedAnswer,
                        userAnswerType: typeof this.selectedAnswer,
                        correctAnswer: this.currentQuestion.answer,
                        correctAnswerType: typeof this.currentQuestion.answer,
                        isCorrect: this.isCorrect
                    });
                    break;
                case 'fill_in_the_blank':
                    // Para preguntas de rellenar espacios, normalizamos y comparamos
                    const userAnswer = this.selectedAnswer ? this.selectedAnswer.trim().toLowerCase() : '';
                    const correctAnswer = this.currentQuestion.answer.trim().toLowerCase();
                    this.isCorrect = userAnswer === correctAnswer;
                    break;
                case 'short_answer':
                    // Para preguntas de respuesta corta, comprobamos si la respuesta contiene palabras clave
                    const userShortAnswer = this.selectedAnswer ? this.selectedAnswer.trim().toLowerCase() : '';
                    const correctShortAnswer = this.currentQuestion.answer.trim().toLowerCase();
                    // Dividir la respuesta correcta en palabras clave
                    const keywords = correctShortAnswer.split(/[,.\s]+/).filter(word => word.length > 3);
                    // Verificar si la respuesta del usuario contiene al menos el 50% de las palabras clave
                    const matchedKeywords = keywords.filter(keyword => userShortAnswer.includes(keyword));
                    const matchPercentage = (matchedKeywords.length / keywords.length) * 100;
                    this.isCorrect = matchPercentage >= 50;
                    break;
                case 'matching_pairs':
                    // Para preguntas de emparejamiento, comparamos los emparejamientos con los correctos
                    const correctMatches = this.currentQuestion.correctMatches;
                    let correctCount = 0;

                    // Comprobar cada emparejamiento
                    Object.keys(this.matchedItems).forEach(termIndex => {
                        if (this.matchedItems[termIndex] === correctMatches[termIndex]) {
                            correctCount++;
                        }
                    });

                    // Si todos los emparejamientos son correctos, la respuesta es correcta
                    this.isCorrect = correctCount === Object.keys(correctMatches).length;

                    // Guardar los emparejamientos como respuesta
                    this.selectedAnswer = JSON.stringify(this.matchedItems);
                    break;
                case 'ordering':
                    // Para preguntas de ordenamiento, comprobamos si el orden es correcto
                    let allCorrect = true;

                    // Marcar cuáles posiciones son correctas
                    this.orderingItems.forEach((item, index) => {
                        const isCorrectPosition = item.originalIndex === index;
                        this.$set(this.orderingItems[index], 'isCorrect', isCorrectPosition);
                        if (!isCorrectPosition) {
                            allCorrect = false;
                        }
                    });

                    this.isCorrect = allCorrect;

                    // Guardar el orden como respuesta
                    this.selectedAnswer = JSON.stringify(this.orderingItems.map(item => item.text));
                    break;
                default:
                    // Para otros tipos de preguntas, comparación exacta
                    this.isCorrect = this.selectedAnswer === this.currentQuestion.answer;
            }

            // Guardar el resultado y la respuesta del usuario
            this.$set(this.questionResults, this.currentIndex, this.isCorrect);

            // Guardar la respuesta específica del usuario en un nuevo objeto
            if (!this.userAnswers) {
                this.userAnswers = {};
            }
            this.$set(this.userAnswers, this.currentIndex, this.selectedAnswer);

            // Hacer scroll a la explicación después de mostrar la respuesta
            this.$nextTick(() => {
                // this.scrollToExplanation();
            });
        },

        scrollToExplanation() {
            // Solo hacer scroll en dispositivos móviles
            if (this.$vuetify.breakpoint.xs) {
                // Usar un timeout para asegurar que el DOM se ha actualizado completamente
                setTimeout(() => {
                    const explanationId = `explanation-${this.currentIndex}`;
                    const explanation = document.getElementById(explanationId);

                    if (!explanation) {
                        console.warn('No se encontró el elemento de explicación');
                        return;
                    }

                    // Intentar diferentes contenedores de scroll en orden de prioridad
                    let scrollContainer = null;
                    const possibleContainers = [
                        document.querySelector('.block-quiz-container'), // Primero intentar el contenedor principal
                        document.querySelector('.scrollable-quiz-content'), // Luego el contenedor scrollable interno
                        this.$refs.scrollableContent, // Referencia directa
                        document.documentElement // Como último recurso, el documento completo
                    ];

                    // Usar el primer contenedor válido
                    for (const container of possibleContainers) {
                        if (container) {
                            scrollContainer = container;
                            break;
                        }
                    }

                    if (scrollContainer) {
                        // Calcular la posición para el scroll
                        const rect = explanation.getBoundingClientRect();

                        // Scroll al elemento directamente
                        explanation.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center'
                        });

                        console.log('Ejecutando scrollIntoView hacia la explicación');
                    } else {
                        console.warn('No se encontró un contenedor de scroll válido');
                    }
                }, 300);
            }
        },

        // Función para animar el scroll manualmente
        smoothScrollTo(element, to, duration) {
            const start = element.scrollTop;
            const change = to - start;
            const startTime = performance.now();

            // Función de animación
            const animateScroll = (currentTime) => {
                const elapsedTime = currentTime - startTime;

                if (elapsedTime > duration) {
                    element.scrollTop = to;
                    return;
                }

                // Cálculo de easing (easeInOutQuad)
                const progress = elapsedTime / duration;
                const easeProgress = progress < 0.5
                    ? 2 * progress * progress
                    : -1 + (4 - 2 * progress) * progress;

                element.scrollTop = start + change * easeProgress;
                requestAnimationFrame(animateScroll);
            };

            requestAnimationFrame(animateScroll);
        },

        nextQuestion() {
            // Si estamos en modo bloque, navegar al siguiente subbloque
            if (this.isBlockMode) {
                this.$emit('navigate-next');
                return;
            }

            if (this.currentIndex < this.allQuestions.length - 1) {
                this.currentIndex++;
                this.resetCurrentQuestion();
                this.scrollToTopOfQuizContainer();
            } else if (this.allQuestionsAnswered) {
                // Si es la última pregunta y todas han sido respondidas,
                // podemos mostrar el botón para ver resultados, pero no cambiamos
                // automáticamente a la pantalla de resultados
            } else {
                // Si estamos en la última pregunta pero hay preguntas sin responder,
                // buscar la primera pregunta sin responder y navegar a ella
                const unansweredIndex = this.findFirstUnansweredQuestion();
                if (unansweredIndex !== -1) {
                    this.currentIndex = unansweredIndex;
                    this.resetCurrentQuestion();
                    this.scrollToTopOfQuizContainer();
                    this.$toast.success('Te llevamos a una pregunta sin responder', {
                        position: 'top-center',
                        color: '#4dd453',
                        timeout: 2000,
                        closeOnClick: true,
                        pauseOnFocusLoss: false,
                        pauseOnHover: true,
                        draggable: true,
                        showCloseButtonOnHover: false,
                        hideProgressBar: true,
                        closeButton: false,
                        icon: true
                    });
                }
            }
        },

        findFirstUnansweredQuestion() {
            // Buscar el índice de la primera pregunta sin responder
            for (let i = 0; i < this.allQuestions.length; i++) {
                if (!this.questionResults.hasOwnProperty(i)) {
                    return i;
                }
            }
            return -1; // No se encontraron preguntas sin responder
        },

        previousQuestion() {
            // Si estamos en modo bloque, navegar al subbloque anterior
            if (this.isBlockMode) {
                this.$emit('navigate-previous');
                return;
            }

            if (this.currentIndex > 0) {
                this.currentIndex--;
                this.resetCurrentQuestion();
                this.scrollToTopOfQuizContainer();
            } else {
                this.$emit('navigate-previous');
            }
        },

        skipQuestion() {
            this.nextQuestion();
        },

        selectOption(option) {
            if (this.isAnswered) return;
            this.selectedAnswer = option;

            // Actualizar el índice seleccionado al seleccionar una opción
            if (this.currentQuestion.type === 'true_false') {
                this.selectedOptionIndex = option === true ? 0 : 1;
            } else {
                const options = this.currentQuestion.type === 'multiple_choice' ? this.currentQuestion.options : ['Verdadero', 'Falso'];
                this.selectedOptionIndex = options.findIndex(opt => opt === option);
            }
            this.checkAnswer();
        },

        getOptionColor(option) {
            if (!this.isAnswered) return 'primary';
            if (option === this.currentQuestion.answer) {
                return 'success';
            }
            return option === this.selectedAnswer ? 'error' : 'primary';
        },

        getDifficultyClass(level) {
            switch (level) {
                case 'easy': return 'level-easy';
                case 'medium': return 'level-medium';
                case 'hard': return 'level-hard';
                default: return 'level-medium';
            }
        },

        translateLevel(level) {
            switch (level) {
                case 'easy': return 'Fácil';
                case 'medium': return 'Media';
                case 'hard': return 'Difícil';
                default: return 'Media';
            }
        },

        saveQuizResults() {
            if (this.isResultSaved) return;
            this.isSaving = true;

            // Preparar los datos a enviar
            const quizData = {
                questions: this.allQuestions.map((question, index) => ({
                    question: question.question,
                    user_answer: this.userAnswers && this.userAnswers[index] !== undefined ? this.userAnswers[index] : null,
                    correct_answer: question.answer,
                    is_correct: this.questionResults[index] || false,
                    type: question.type,
                    level: question.level,
                    options: question.options || []
                }))
            };

            // Enviar los datos al servidor
            this.axios.post('/quiz-results', {
                documentId: this.$route.params.documentId,
                score: this.scorePercentage,
                totalQuestions: this.allQuestions.length,
                correctAnswers: this.correctAnswers,
                quizData: quizData
            }, {
                headers: { Authorization: this.$root.token.value }
            })
                .then(response => {
                    if (response.data.success) {
                        this.isResultSaved = true;
                    } else {
                        this.$toast.error(response.data.error || 'Error al guardar los resultados');
                    }
                })
                .catch(error => {
                    console.error('Error al guardar los resultados:', error);
                    this.$toast.error('Error al guardar los resultados. Por favor, inténtalo de nuevo.');
                })
                .finally(() => {
                    this.isSaving = false;
                    // Marcar el cuestionario como terminado para mostrar la pantalla de resultados
                    this.isQuizFinished = true;
                });
        },

        getAnswerForQuestion(index) {
            // Este método obtiene la respuesta del usuario para una pregunta específica
            const question = this.allQuestions[index];
            if (!question) return null;

            // Usar el objeto userAnswers para obtener la respuesta específica
            if (this.userAnswers && this.userAnswers[index] !== undefined) {
                const userAnswer = this.userAnswers[index];

                // Formatear la respuesta según el tipo de pregunta
                switch (question.type) {
                    case 'true_false':
                        return userAnswer;
                    case 'matching_pairs':
                        try {
                            const matches = JSON.parse(userAnswer);
                            // Crear un texto que muestre los emparejamientos
                            return Object.keys(matches).map(termIndex => {
                                const valueIndex = matches[termIndex];
                                return `${question.terms[termIndex]} → ${question.values[valueIndex]}`;
                            }).join(', ');
                        } catch (e) {
                            return 'Error en formato de respuesta';
                        }

                    case 'ordering':
                        try {
                            const order = JSON.parse(userAnswer);
                            // Mostrar el orden que dio el usuario
                            return order.join(' → ');
                        } catch (e) {
                            return 'Error en formato de respuesta';
                        }

                    default:
                        return userAnswer;
                }
            }
            return null;
        },

        goToQuestion(index) {
            if (index >= 0 && index < this.allQuestions.length) {
                this.currentIndex = index;
                this.resetCurrentQuestion();
                this.scrollToTopOfQuizContainer();
            }
        },

        toggleQuestionView() {
            // Esta función podría cambiar entre vista de una pregunta y vista de cuadrícula
            console.log('Cambiar vista de preguntas');
        },

        focusQuizContainer() {
            this.$nextTick(() => {
                if (this.$refs.quizContainer) {
                    this.$refs.quizContainer.focus();
                }
            });
        },

        selectPrevOption() {
            if (this.isAnswered || !['multiple_choice', 'true_false'].includes(this.currentQuestion.type)) return;

            // Obtener las opciones según el tipo de pregunta
            const options = this.currentQuestion.type === 'multiple_choice'
                ? this.currentQuestion.options
                : this.getOptionsArray();

            // Decrementar el índice seleccionado
            if (this.selectedOptionIndex === null) {
                this.selectedOptionIndex = 0;
            } else {
                this.selectedOptionIndex = (this.selectedOptionIndex - 1 + options.length) % options.length;
            }

            this.selectedAnswer = options[this.selectedOptionIndex];
        },

        selectNextOption() {
            if (this.isAnswered || !['multiple_choice', 'true_false'].includes(this.currentQuestion.type)) return;

            // Obtener las opciones según el tipo de pregunta
            const options = this.currentQuestion.type === 'multiple_choice'
                ? this.currentQuestion.options
                : this.getOptionsArray();

            // Incrementar el índice seleccionado
            if (this.selectedOptionIndex === null) {
                this.selectedOptionIndex = 0;
            } else {
                this.selectedOptionIndex = (this.selectedOptionIndex + 1) % options.length;
            }

            this.selectedAnswer = options[this.selectedOptionIndex];
        },

        handleEnterKey() {
            if (this.isAnswered) {
                this.nextQuestion();
            } else {
                if (!this.selectedAnswer) {
                    this.selectedAnswer = '';
                }
                this.checkAnswer();
            }
        },

        handleKeyboardEvent(event) {

            this.showKeyboard = true;
            switch (event.code) {
                case 'ArrowLeft':
                    this.previousQuestion();
                    break;
                case 'ArrowRight':
                    this.nextQuestion();
                    break;
                case 'ArrowUp':
                    this.selectPrevOption();
                    break;
                case 'ArrowDown':
                    this.selectNextOption();
                    break;
                case 'Enter':
                    if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA')
                        break;

                    this.handleEnterKey();
                    break;
                case 'Space':
                    if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA')
                        break;
                    this.handleEnterKey();
                    break;
            }
        },

        reviewAnswers() {
            // Activar el modo de revisión
            this.reviewMode = true;
        },

        loadQuizResults() {
            if (!this.quizResultData || !this.quizResultData.quiz_data) return;

            // Cargar las preguntas y respuestas del resultado
            const resultData = this.quizResultData.quiz_data || {};
            const resultQuestions = resultData.questions || [];

            // Si hay preguntas en el resultado
            if (resultQuestions.length > 0) {
                // Mapear las preguntas del resultado al formato esperado
                this.allQuestions = resultQuestions.map(q => {
                    return {
                        question: q.question,
                        answer: q.correct_answer,
                        type: q.type || 'multiple_choice',
                        explanation: q.explanation || '',
                        options: q.options || (q.type === 'true_false' ? { true: 'Verdadero', false: 'Falso' } : [])
                    };
                });

                // Cargar los resultados y respuestas de cada pregunta
                resultQuestions.forEach((q, index) => {
                    this.$set(this.questionResults, index, q.is_correct);

                    // También cargar las respuestas específicas del usuario
                    if (!this.userAnswers) {
                        this.userAnswers = {};
                    }

                    this.$set(this.userAnswers, index, q.user_answer);
                });

                // Actualizar estadísticas
                this.isQuizFinished = true;
                this.isResultSaved = true;
            }
        },

        // Métodos para preguntas de emparejamiento
        startDrag(index, type) {
            // Evitar la selección de texto durante el arrastre
            document.body.style.userSelect = 'none';

            if (type === 'term') {
                // Si el término ya está emparejado, primero remover el emparejamiento
                if (this.matchedItems[index] !== undefined) {
                    this.removePair(index);
                }

                this.activeTerm = index;
                this.termBeingDragged = index;
                this.dragActive = true;

                // Inicializar coordenadas de la línea
                const termEl = this.$refs[`term-${index}`][0];
                const rect = termEl.getBoundingClientRect();
                const svgRect = this.$refs.linesSvg.getBoundingClientRect();

                // Punto inicial de la línea (centro derecho del término)
                this.dragLineCoords.x1 = rect.right - svgRect.left;
                this.dragLineCoords.y1 = rect.top + (rect.height / 2) - svgRect.top;

                // Inicialmente el punto final es igual al inicial
                this.dragLineCoords.x2 = this.dragLineCoords.x1;
                this.dragLineCoords.y2 = this.dragLineCoords.y1;

                // Agregar event listeners para el movimiento
                window.addEventListener('mousemove', this.handleMouseMove);
                window.addEventListener('mouseup', this.handleMouseUp);
            }
        },

        handleMouseMove(event) {
            if (!this.dragActive) return;

            const svgRect = this.$refs.linesSvg.getBoundingClientRect();

            // Actualizar el punto final de la línea a la posición actual del mouse
            this.dragLineCoords.x2 = event.clientX - svgRect.left;
            this.dragLineCoords.y2 = event.clientY - svgRect.top;
        },

        handleValueHover(valueIndex) {
            if (this.dragActive && this.termBeingDragged !== null) {
                this.activeValue = valueIndex;

                // Actualizar línea para que termine en el centro izquierdo del valor
                const valueEl = this.$refs[`value-${valueIndex}`][0];
                const rect = valueEl.getBoundingClientRect();
                const svgRect = this.$refs.linesSvg.getBoundingClientRect();

                this.dragLineCoords.x2 = rect.left - svgRect.left;
                this.dragLineCoords.y2 = rect.top + (rect.height / 2) - svgRect.top;
            }
        },

        handleTouchMove(event) {
            if (!this.dragActive) return;

            const touch = event.touches[0];
            const svgRect = this.$refs.linesSvg.getBoundingClientRect();

            // Actualizar el punto final de la línea a la posición actual del toque
            this.dragLineCoords.x2 = touch.clientX - svgRect.left;
            this.dragLineCoords.y2 = touch.clientY - svgRect.top;

            // Verificar si está sobre un valor
            for (let i = 0; i < this.currentQuestion.values.length; i++) {
                const valueEl = this.$refs[`value-${i}`][0];
                const rect = valueEl.getBoundingClientRect();

                if (
                    touch.clientX >= rect.left &&
                    touch.clientX <= rect.right &&
                    touch.clientY >= rect.top &&
                    touch.clientY <= rect.bottom
                ) {
                    this.handleValueHover(i);
                    break;
                }
            }
        },

        handleMouseUp() {
            // Limpiar los event listeners
            window.removeEventListener('mousemove', this.handleMouseMove);
            window.removeEventListener('mouseup', this.handleMouseUp);

            // Finalizar el arrastre sin crear un emparejamiento
            if (this.dragActive) {
                this.dragActive = false;
                this.activeTerm = null;
                this.activeValue = null;
                this.termBeingDragged = null;
            }

            // Restaurar la selección de texto
            document.body.style.userSelect = '';
        },

        endDrag(valueIndex) {
            if (!this.dragActive || this.termBeingDragged === null) return;

            // Si el valor ya está emparejado con otro término, no hacer nada
            if (Object.values(this.matchedItems).includes(valueIndex)) {
                this.dragActive = false;
                this.activeTerm = null;
                this.termBeingDragged = null;
                return;
            }

            // Crear el emparejamiento
            this.createMatch(this.termBeingDragged, valueIndex);

            // Finalizar el arrastre
            this.dragActive = false;
            this.activeTerm = null;
            this.activeValue = null;
            this.termBeingDragged = null;

            // Restaurar la selección de texto
            document.body.style.userSelect = '';

            // Eliminar event listeners
            window.removeEventListener('mousemove', this.handleMouseMove);
            window.removeEventListener('mouseup', this.handleMouseUp);

            // Actualizar las coordenadas de todas las líneas
            this.$nextTick(() => {
                this.updateMatchingLines();
            });
        },

        removePair(termIndex) {
            if (this.matchedItems[termIndex] === undefined) return;

            const valueIndex = this.matchedItems[termIndex];

            // Remover el emparejamiento
            delete this.matchedItems[termIndex];

            // Remover la línea de emparejamiento
            const matchIndex = this.userMatches.findIndex(match =>
                match.termIndex === termIndex && match.valueIndex === valueIndex);
            if (matchIndex !== -1) {
                this.userMatches.splice(matchIndex, 1);
            }

            // Actualizar las coordenadas de las líneas restantes
            this.$nextTick(() => {
                this.updateMatchingLines();
            });
        },

        createMatch(termIndex, valueIndex) {
            // Guardar el emparejamiento
            this.$set(this.matchedItems, termIndex, valueIndex);

            // Calcular las coordenadas de la línea
            const termEl = this.$refs[`term-${termIndex}`][0];
            const valueEl = this.$refs[`value-${valueIndex}`][0];
            const svgRect = this.$refs.linesSvg.getBoundingClientRect();

            const termRect = termEl.getBoundingClientRect();
            const valueRect = valueEl.getBoundingClientRect();

            // Añadir la línea de emparejamiento con sus coordenadas
            this.userMatches.push({
                termIndex: termIndex,
                valueIndex: valueIndex,
                x1: termRect.right - svgRect.left,
                y1: termRect.top + (termRect.height / 2) - svgRect.top,
                x2: valueRect.left - svgRect.left,
                y2: valueRect.top + (valueRect.height / 2) - svgRect.top
            });
        },

        updateMatchingLines() {
            if (!this.$refs.linesSvg) return;

            const svgRect = this.$refs.linesSvg.getBoundingClientRect();

            // Actualizar coordenadas de todas las líneas
            this.userMatches.forEach((match, index) => {
                const termEl = this.$refs[`term-${match.termIndex}`][0];
                const valueEl = this.$refs[`value-${match.valueIndex}`][0];

                const termRect = termEl.getBoundingClientRect();
                const valueRect = valueEl.getBoundingClientRect();

                // Actualizar coordenadas
                this.$set(this.userMatches[index], 'x1', termRect.right - svgRect.left);
                this.$set(this.userMatches[index], 'y1', termRect.top + (termRect.height / 2) - svgRect.top);
                this.$set(this.userMatches[index], 'x2', valueRect.left - svgRect.left);
                this.$set(this.userMatches[index], 'y2', valueRect.top + (valueRect.height / 2) - svgRect.top);
            });
        },

        clearLastMatch() {
            if (this.userMatches.length > 0) {
                const lastMatch = this.userMatches.pop();
                delete this.matchedItems[lastMatch.termIndex];
            }
        },

        clearAllMatches() {
            this.userMatches = [];
            this.matchedItems = {};
            this.activeTerm = null;
            this.activeValue = null;
            this.dragActive = false;
            this.termBeingDragged = null;
        },

        // Métodos para preguntas de ordenamiento
        initOrderingItems() {
            if (this.currentQuestion.type !== 'ordering' || !this.currentQuestion.items) {
                return;
            }

            // Si la pregunta ya ha sido respondida, intentar recuperar el orden del usuario
            if (this.isAnswered && this.selectedAnswer) {
                try {
                    const userOrder = JSON.parse(this.selectedAnswer);
                    const userOrderItems = [];

                    // Reconstruir los elementos en el orden del usuario
                    userOrder.forEach((text) => {
                        const originalItem = this.currentQuestion.items.find(item => item.text === text);
                        if (originalItem) {
                            userOrderItems.push({
                                text: originalItem.text,
                                originalIndex: originalItem.originalIndex,
                                isCorrect: false // Se actualizará más tarde
                            });
                        }
                    });

                    if (userOrderItems.length === this.currentQuestion.items.length) {
                        this.orderingItems = userOrderItems;
                        return;
                    }
                } catch (e) {
                    console.error('Error al reconstruir el orden del usuario:', e);
                }
            }

            // Si no es una pregunta respondida o hubo un error, crear una copia del array de ítems y mezclarlos
            const shuffledItems = [...this.currentQuestion.items];

            // Si no está respondida, mezclar los elementos, de lo contrario, preservar el orden original
            if (!this.isAnswered) {
                // Algoritmo de Fisher-Yates para mezclar el array
                for (let i = shuffledItems.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [shuffledItems[i], shuffledItems[j]] = [shuffledItems[j], shuffledItems[i]];
                }
            }

            this.orderingItems = shuffledItems.map(item => ({
                text: item.text,
                originalIndex: item.originalIndex,
                isCorrect: false
            }));
        },

        getCorrectOrderingItems() {
            if (!this.currentQuestion || !this.currentQuestion.items) {
                return [];
            }

            // Crear una copia de los elementos
            const items = [...this.currentQuestion.items];

            // Ordenar los elementos por su índice original (que representa el orden correcto)
            const sortedItems = items.sort((a, b) => {
                if (a.originalIndex !== undefined && b.originalIndex !== undefined) {
                    return a.originalIndex - b.originalIndex;
                }
                return 0;
            });

            return sortedItems;
        },

        handleResize() {
            if (this.currentQuestion && this.currentQuestion.type === 'matching_pairs') {
                this.updateMatchingLines();
            }
        },

        // Métodos para preguntas de emparejamiento con clic
        handleTermClick(index) {
            // Si ya está arrastrando, no hacer nada
            if (this.dragActive) return;

            // Si el término ya está emparejado, remover el emparejamiento
            if (this.matchedItems[index] !== undefined) {
                this.removePair(index);
                return;
            }

            // Si ya hay un término activo, desactivarlo
            if (this.activeTerm !== null && this.activeTerm !== index) {
                this.activeTerm = null;
            }

            // Activar/desactivar el término
            if (this.activeTerm === index) {
                this.activeTerm = null;
                this.dragLineCoords = { x1: 0, y1: 0, x2: 0, y2: 0 };
            } else {
                this.activeTerm = index;
                this.termBeingDragged = index;

                // Calcular coordenadas de la línea inicial
                const termEl = this.$refs[`term-${index}`][0];
                const rect = termEl.getBoundingClientRect();
                const svgRect = this.$refs.linesSvg.getBoundingClientRect();

                // Punto inicial de la línea (centro derecho del término)
                this.dragLineCoords.x1 = rect.right - svgRect.left;
                this.dragLineCoords.y1 = rect.top + (rect.height / 2) - svgRect.top;

                // El punto final inicialmente es el mismo
                this.dragLineCoords.x2 = this.dragLineCoords.x1;
                this.dragLineCoords.y2 = this.dragLineCoords.y1;
            }
        },

        handleValueClick(index) {
            // Si no hay un término activo o ya está arrastrando, no hacer nada
            if (this.activeTerm === null || this.dragActive) return;

            // Si el valor ya está emparejado, no hacer nada
            if (Object.values(this.matchedItems).includes(index)) return;

            // Crear el emparejamiento
            this.createMatch(this.activeTerm, index);

            // Resetear estados
            this.activeTerm = null;
            this.termBeingDragged = null;
            this.dragLineCoords = { x1: 0, y1: 0, x2: 0, y2: 0 };
        },

        // Método para obtener el array de opciones para preguntas de verdadero/falso
        getOptionsArray() {
            if (this.currentQuestion.type !== 'true_false') return [];

            // Si hay opciones definidas en el objeto options, las usamos
            if (this.currentQuestion.options) {
                const options = this.currentQuestion.options;
                // Si options es un objeto con campos true y false
                if (options.true && options.false) {
                    return [options.true, options.false];
                }
                // Si options es un array directamente
                else if (Array.isArray(options)) {
                    return options;
                }
            }

            // Si no hay opciones definidas, usamos valores predeterminados
            return ['Verdadero', 'Falso'];
        },

        goToPremium() {
            // Navegar a la página de facturación/premium
            this.openFeaturesDialog('quiz_explanation_premium_cta');
        },

        openFeaturesDialog(origin) {
            this.$router.push({ query: { ...this.$route.query, premium_from: origin } }).catch(err => {
                if (err.name !== 'NavigationDuplicated') {
                    console.error(err);
                }
            });
            this.showFeaturesDialog = true;
        },

        // Hace scroll al inicio del contenedor principal del quiz
        scrollToTopOfQuizContainer() {
            // Busca el contenedor principal del bloque
            const container = document.querySelector('.block-quiz-container');
            if (container) {
                container.scrollTo({ top: 0, left: 0 });
            }
        },

        shouldBlurExplanation() {
            return false;
            
            // Aproximadament 70% de les explicacions estaran blurrades
            // Es manté determinista segons currentIndex
            const value = (this.currentIndex * 7 + 13) % 10;
            return !this.isCorrect && value < 7;
        },

    },
    mounted() {
        // Agregar los event listeners para teclado
        window.addEventListener('keydown', this.handleKeyboardEvent);

        // Agregar listener para redimensionamiento de ventana
        window.addEventListener('resize', this.handleResize);

        this.checkIfQuizIsComplete();

    },
    beforeDestroy() {
        // Eliminar los event listeners cuando el componente se destruye
        window.removeEventListener('keydown', this.handleKeyboardEvent);
        window.removeEventListener('resize', this.handleResize);
        window.removeEventListener('mousemove', this.handleMouseMove);
        window.removeEventListener('mouseup', this.handleMouseUp);
    },

    // Actualizar la posición de las líneas cuando cambia el componente o la ventana se redimensiona
    updated() {
        if (this.currentQuestion && this.currentQuestion.type === 'matching_pairs') {
            this.$nextTick(() => {
                this.updateMatchingLines();
            });
        }
    },
};
</script>

<style scoped>
.quiz-single-question {
    /* padding-bottom: 40px; */
    /* Removed padding */
    height: 100%;
    /* Ensure the main container takes full height */
    display: flex;
    flex-direction: column;
}

.scrollable-quiz-content {
    flex: 1;
    /* Take available vertical space */
    overflow-y: auto;
    /* Enable vertical scrolling */
    /* Add some padding around the content */
    position: relative;
    /* Needed if children use absolute positioning relative to content */
}

.quiz-pagination-container {
    flex-shrink: 0;
    /* Prevent pagination from shrinking */
    background-color: #fff;
    border-top: 1px solid #e0e0e0;
    padding-top: 20px;
    /* Ensure background for pagination area */
    /* Optional: add border or shadow for separation */
    /* border-top: 1px solid #e0e0e0; */
}

.quiz-results {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 2rem;
    max-width: 800px;
    margin: 0 auto;
    width: 100%;
}

.result-icon {
    width: 100px;
    height: 100px;
    background-color: #dbffd4;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #4dd453;
    box-shadow: 0 0 0 10px rgba(85, 206, 35, 0.1);
}

.key-improvement-area {
    border-radius: 20px !important;
    padding: 1rem;
    width: 100%;
    max-width: 600px;
    background-color: #f6faf5 !important;
    border: 1px solid #d6e9daab !important;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08) !important;
    transition: all 0.3s ease;
}

.key-improvement-area:hover {
    transform: translateY(-5px);
}

.action-buttons {
    margin-top: 2rem;
}

.action-btn {
    margin: 0.5rem;
    min-width: 180px;
}

.progress-text {
    min-width: 120px;
    font-weight: bold;
}

.question-card {
    box-shadow: none !important;
}

.question-title {
    position: relative;
    align-items: flex-start;
    font-size: 1.4rem;
    /* Reducido de 1.6rem */
    font-weight: 500;
    font-family: -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, Ubuntu, sans-serif;
    color: #3C3C3C;
    line-height: 1.4;
    flex: 1;
    white-space: pre-wrap;
    /* Preservar espacios en blanco */
}

/* Nuevo: Estilos específicos para elementos subrayados */
.question-title>>>u {
    white-space: nowrap;
    /* Evitar saltos de línea dentro del subrayado */
    word-wrap: break-word;
    /* No romper palabras */
    display: inline;
    /* Mantener como elemento en línea */
    text-decoration: underline;
    /* Asegurar que siga subrayado */
}

.primary-btn {
    border-radius: 10px !important;
    text-transform: none !important;
    letter-spacing: 0 !important;
    font-weight: 600 !important;
    font-size: 0.9rem !important;
    transition: filter 0.2s ease, transform 0.1s ease !important;
    height: 36px !important;
    background-color: #4DD453 !important;
    box-shadow: 0 4px 0 #59A700 !important;
    margin-top: -10px;
    position: relative;
    color: #fff !important;
    z-index: 2;
}

.primary-btn-next {
    border-radius: 10px !important;
    text-transform: none !important;
    letter-spacing: 0 !important;
    font-weight: 600 !important;
    font-size: 0.95rem !important;
    transition: filter 0.2s ease, transform 0.1s ease !important;
    height: 44px !important;
    margin-top: -4px !important;
    background-color: #4DD453 !important;
    box-shadow: 0 4px 0 #59A700 !important;
    position: relative;
    color: #fff !important;
    z-index: 2;
}

.question-number {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background-color: #1976d2;
    /* color: white; */
    font-weight: bold;
    font-size: 14px;
    flex-shrink: 0;
}

.level-badge {
    padding: 4px 8px;
    font-size: 12px;
    font-weight: bold;
    color: white;
    position: absolute;
    top: 10px;
    right: 10px;
    border-radius: 4px;
}

.level-easy {
    background-color: #4CAF50;
}

.level-medium {
    background-color: #FFC107;
}

.level-hard {
    background-color: #F44336;
}

.answer-feedback {
    font-size: 1.1rem;
    /* Reducido de 1.2rem */
    font-weight: 500;
    font-family: -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, Ubuntu, sans-serif;
    color: #3C3C3C;
    line-height: 1.4;
    /* Ensure it doesn't get hidden by pagination */
    position: relative;
    z-index: 1;
}

.stat-item {
    text-align: center;
    padding: 0 16px;
}

.stat-value {
    font-size: 22px;
    /* Reducido de 24px */
    font-weight: bold;
    color: #1976d2;
}

.stat-label {
    font-size: 14px;
    color: #666;
}

/* Estilos para tarjetas de opción */
.option-cards-container {
    margin-top: 16px;
}

.option-card {
    border: 2px solid #E5E5E5;
    box-shadow: 0 3px 0 rgb(0 0 0 / 15%) !important;
    border-radius: 16px !important;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-bottom: 12px;
    text-align: center;
    font-size: 1.2rem;
    user-select: none;
    /* <-- AÑADIDO: Deshabilitar selección de texto */
}


.option-correct {
    background-color: rgba(76, 175, 80, 0.1);
    background-color: #e8ffdf !important;
    border: 2px solid #4dd453 !important;
    box-shadow: 0 3px 0 #70d100 !important;
}

.option-correct::before,
.option-incorrect::before,
.option-selected::before,
.option-card::before {
    opacity: 0;
}

.option-incorrect {
    background-color: rgba(244, 67, 54, 0.1) !important;
    border-color: #F44336 !important;
    box-shadow: 0 3px 0 #F44336 !important;
}

.option-number {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: 2px solid #E5E5E5;
    color: #AFAFAF;
    font-weight: bold;
    font-size: 14px;
    flex-shrink: 0;
}

.option-correct .option-text {
    color: #2d8930;
}

.option-correct .option-number {
    color: #4CAF50;
    border: 2px solid #acda9a;
}

.option-incorrect .option-text {
    color: #F44336;
}

.option-incorrect .option-number {
    color: #F44336;
    border: 2px solid #ffc4c0;
}

.option-text {
    flex-grow: 1;
    font-size: 1rem;
    /* Reducido de 1.1rem */
    font-weight: 500;
    font-family: -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, Ubuntu, sans-serif;
    color: #4B4B4B;
}

/* Estilos para la paginación en la parte inferior */
.quiz-pagination {
    /* position: absolute; */
    /* Removed */
    /* bottom: 0; */
    /* Removed */
    left: 0;
    right: 0;
    width: 100%;
    display: flex;
    /* Keep flex for internal alignment */
    justify-content: center;
    /* Keep centering */
}

.page-number {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 2px 3px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    font-weight: 500;
    font-family: -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, Ubuntu, sans-serif;
    border: 2px solid #ededed;
}

.page-number:hover {
    background-color: #f5f5f5;
}

.page-number.active {
    color: #4B4B4B !important;
}

.page-number.not-answered {
    color: #c6c6c6 !important;
}

.page-number.completed {
    background-color: #e8ffdf !important;
    border: 2px solid #4dd453 !important;
    color: #4dd453 !important;
}

.page-number.error {
    background-color: rgba(244, 67, 54, 0.1) !important;
    border-color: #F44336 !important;
    color: #F44336 !important;
}

.text-success {
    color: #4DD453;
}

.text-error {
    color: #F44336;
}

.no-answer-text {
    color: #757575;
    font-size: 14px;
    margin-bottom: 0;
}

.progress-bar>>>.v-progress-linear__determinate {
    border-radius: 10px !important;
}

.progress-bar>>>.v-progress-linear__buffer {
    background-color: #e2e2e2 !important;
}

/* Estilos para centrar el menú y quitar la sombra */
::v-deep .question-menu {
    box-shadow: none !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
}

::v-deep .v-menu__content {
    box-shadow: none !important;
    border: 2px solid #ededed;
}

::v-deep .no-shadow {
    box-shadow: none !important;
}

.option-keyboard-selected {
    /* border: 2px solid #757575 !important; */
    transform: translateY(-2px);
    box-shadow: 0 5px 0 rgba(0, 0, 0, 0.2) !important;
}

.option-selected {
    /* border: 2px solid #757575 !important; */
}

.premium-btn {
    text-transform: none !important;
    letter-spacing: 0 !important;
    font-weight: 800 !important;
    font-size: 0.95rem !important;
    /* Reducido de 1.05rem */
    border-radius: 16px !important;
    transition: filter 0.2s ease, transform 0.1s ease !important;
    border: 2px solid #4dd453 !important;
    height: 44px !important;
    padding-left: 24px !important;
    padding-right: 24px !important;
    background-color: #4dd453 !important;
    box-shadow: 0 4px 0 #42b848 !important;
    color: white !important;
}

.regular-btn {
    text-transform: none !important;
    letter-spacing: 0 !important;
    font-weight: 600 !important;
    font-size: 0.95rem !important;
    /* Reducido de 1.05rem */
    border-radius: 16px !important;
    transition: filter 0.2s ease, transform 0.1s ease !important;
    border: 1px solid #e0e0e0 !important;
    height: 46px !important;
    padding-left: 24px !important;
    padding-right: 24px !important;
    background-color: white !important;
    box-shadow: 0 4px 0 #b5b5b5 !important;
    color: #424242 !important;
}

.regular-btn-previous {
    text-transform: none !important;
    letter-spacing: 0 !important;
    font-weight: 600 !important;
    font-size: 0.95rem !important;
    /* Reducido de 1.05rem */
    border-radius: 10px !important;
    transition: filter 0.2s ease, transform 0.1s ease !important;
    border: 1px solid #e0e0e0 !important;
    height: 46px !important;
    background-color: white !important;
    box-shadow: 0 4px 0 #b5b5b5 !important;
    color: #424242 !important;
    margin-top: -4px !important;
}

.regular-btn-primary {
    text-transform: none !important;
    letter-spacing: 0 !important;
    font-weight: 600 !important;
    font-size: 0.95rem !important;
    /* Reducido de 1.05rem */
    border-radius: 16px !important;
    transition: filter 0.2s ease, transform 0.1s ease !important;
    border: 1px solid #4dd453 !important;
    height: 46px !important;
    padding-left: 24px !important;
    padding-right: 24px !important;
    background-color: #4dd453 !important;
    box-shadow: 0 4px 0 #42b848 !important;
    color: white !important;
}

.font-rubik {
    font-family: -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, Ubuntu, sans-serif !important;
}

.review-questions-container {
    width: 100%;
    max-width: 800px;
    margin: 0 auto;
}

.review-question-card {
    border-radius: 12px;
    padding: 16px;
    background-color: #ffffff;
    border: 1px solid #e6e6e6;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;
    position: relative;
}

.review-question-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.question-result-icon {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    flex-shrink: 0;
}

.question-result-icon svg.text-success {
    color: #4dd453;
}

.question-result-icon svg.text-error {
    color: #f44336;
}

.question-text {
    font-weight: 500;
    font-size: 0.9rem;
    /* Reducido de 1rem */
    color: #3C3C3C;
    line-height: 1.4;
    flex: 1;
    white-space: pre-wrap;
    /* Preservar espacios en blanco */
}

.question-details {
    background-color: #f9f9f9;
    padding: 16px;
    border-radius: 8px;
    margin-left: 32px;
}

.text-success {
    color: #4dd453 !important;
    font-weight: 500;
}

.text-error {
    color: #f44336 !important;
    font-weight: 500;
}

.results-btn {
    background-color: #4dd453 !important;
    box-shadow: 0 4px 0 #42b848 !important;
    font-weight: 600 !important;
    font-size: 1rem !important;
}

.results-btn:hover {
    transform: translateY(-2px);
    filter: brightness(1.05);
}

.quiz-dialog {
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    border: 1px solid #eaeaea;
}

.text-primary {
    color: #4dd453 !important;
}

/* Estilos para preguntas de emparejamiento */
.matching-container {
    margin-top: 20px;
    user-select: none;
}

.matching-instructions {
    font-size: 0.9rem;
    /* Reducido de 1rem */
    color: #666;
    text-align: center;
}

.matching-pairs-container {
    position: relative;
    min-height: 250px;
}

.matching-row {
    align-items: center;
    min-height: 250px;
}

.matching-column {
    display: flex;
    flex-direction: column;
    z-index: 2;
    justify-content: center;
}

.middle-column {
    z-index: 1;
    align-self: stretch;
}

.matching-item {
    border: 2px solid #E5E5E5;
    border-radius: 12px;
    padding: 10px;
    margin-bottom: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    background-color: white;
    position: relative;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
    user-select: none;
}

.matching-item:hover:not(.matched):not(.disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    border-color: #c4c4c4;
}

.matching-item.matched {
    border-color: #4dd453;
    background-color: #e8ffdf;
}

.matching-item.active {
    border-color: #1976d2;
    box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.3);
    z-index: 3;
}

.matching-item.drop-target {
    border-color: #1976d2;
    background-color: rgba(25, 118, 210, 0.1);
}

.matching-item.disabled {
    cursor: default;
    opacity: 0.9;
}

.matching-content {
    display: flex;
    align-items: center;
    user-select: none;
    min-height: 36px;
}

.term-indicator,
.value-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: 2px solid #E5E5E5;
    color: #AFAFAF;
    font-weight: bold;
    font-size: 14px;
    flex-shrink: 0;
}

.term-text,
.value-text {
    flex-grow: 1;
    font-size: 0.9rem;
    /* Reducido de 0.95rem */
    font-weight: 500;
    font-family: -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, Ubuntu, sans-serif;
    color: #4B4B4B;
    padding: 0 10px;
    user-select: none;
}

.term-item .matching-content {
    justify-content: flex-start;
}

.value-item .matching-content {
    justify-content: flex-end;
}

.matching-lines-container {
    position: relative;
    width: 100%;
    height: 100%;
}

.matching-lines-svg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    pointer-events: none;
}

.matching-line {
    stroke: #1976d2;
    stroke-width: 2px;
    pointer-events: none;
}

.active-line {
    stroke: #4dd453;
    stroke-width: 3px;
    stroke-dasharray: none;
}

@media (max-width: 960px) {
    .matching-item {
        margin-bottom: 6px;
        padding: 8px;
    }

    .term-text,
    .value-text {
        font-size: 0.8rem;
        /* Reducido de 0.85rem */
    }

    .term-indicator,
    .value-indicator {
        width: 24px;
        height: 24px;
        font-size: 12px;
    }

    .matching-instructions {
        font-size: 0.85rem;
        /* Reducido de 0.9rem */
    }
}

/* Estilos para preguntas de ordenamiento */
.ordering-container {
    margin-top: 20px;
}

.ordering-instructions {
    font-size: 1rem;
    color: #666;
    text-align: center;
    margin-bottom: 20px;
}

.ordering-items-container {
    padding: 16px;
    border: 1px solid #e5e5e5;
    border-radius: 12px;
    background-color: #f9f9f9;
}

.ordering-list {
    min-height: 250px;
    width: 100%;
}

.ordering-item-content {
    min-height: 48px;
    height: 100%;
    padding: 16px !important;
    user-select: none;
    /* Evitar selección de texto */
}

.ordering-item-wrapper {
    margin-bottom: 16px;
    width: 100%;
}

.ordering-item {
    border: 2px solid #E5E5E5;
    border-radius: 12px;
    background-color: white;
    cursor: grab;
    transition: all 0.2s ease;
    min-height: 80px;
    /* Altura mínima consistente */
    height: 100%;
    /* Ocupar toda la altura disponible */
    display: flex;
    flex-direction: column;
    width: 100%;
    user-select: none;
    /* Evitar selección de texto */
}

.ordering-item:hover:not(.sortable-ghost) {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.ordering-item.sortable-ghost {
    opacity: 0.5;
    background-color: #e0e0e0;
}

.ordering-item.correct-position {
    border-color: #4dd453;
    background-color: #e8ffdf;
}

.ordering-item.incorrect-position {
    border-color: #F44336;
    background-color: rgba(244, 67, 54, 0.1);
}

.drag-handle {
    cursor: grab;
    color: #757575;
}

.position-indicator {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.item-text {
    flex-grow: 1;
    font-size: 0.9rem;
    /* Reducido de 1rem */
    font-weight: 500;
    font-family: -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, Ubuntu, sans-serif;
    color: #4B4B4B;
    word-break: break-word;
    /* Evitar que el texto se salga */
    white-space: normal;
    /* Permitir múltiples líneas */
}

.ordering-wrapper {
    position: relative;
    width: 100%;
    display: flex;
}

.ordering-numbers {
    position: relative;
    width: 40px;
    margin-right: 16px;
    display: flex;
    flex-direction: column;
    padding-top: 3px;
    /* Ajuste fino para alineación */
}

.order-number-container {
    min-height: 80px;
    /* Altura mínima aumentada */
    display: flex;
    align-items: flex-start;
    padding-top: 16px;
    /* Alineación con el elemento */
    margin-bottom: 16px;
    /* Espacio entre números para coincidir con el espacio entre elementos */
}

.ordering-items-draggable {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.ordering-items-draggable>.ordering-list>div {
    margin-bottom: 16px;
    /* Espacio consistente entre elementos */
}

.ordering-items-draggable>.ordering-list>div:last-child {
    margin-bottom: 0;
    /* Eliminar margen del último elemento */
}

.order-number {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: 2px solid #E5E5E5;
    color: #424242;
    font-weight: bold;
    font-size: 14px;
    flex-shrink: 0;
    background-color: white;
}

.order-number-correct {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: 2px solid #acda9a;
    color: #4CAF50;
    font-weight: bold;
    font-size: 14px;
    flex-shrink: 0;
    background-color: white;
}

.correct-order-item {
    padding: 8px;
    border-radius: 8px;
    background-color: #f8fff8;
    margin-bottom: 4px;
}

.drag-indicator {
    color: #757575;
    flex-shrink: 0;
}

/* Estilos para la retroalimentación de emparejamiento */
.correct-match-item {
    background-color: #f5f9ff;
    border-radius: 8px;
    padding: 8px 12px;
    margin-bottom: 6px;
    border-left: 3px solid #4dd453;
}

.match-item-term,
.match-item-value {
    display: flex;
    align-items: center;
    font-size: 0.95rem;
}

.match-arrow {
    color: #1976d2;
    font-weight: bold;
}

.match-indicator {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 4px;
    background-color: #e8f5ff;
    border: 1px solid #c4e3ff;
    font-size: 12px;
    font-weight: bold;
    color: #1976d2;
}

/* Estilos para la explicación premium */
.explanation-container {
    position: relative;
    margin-top: 18px !important;
    /* Espacio adicional si es necesario */
    scroll-margin-top: 80px;
    /* Para scroll nativo */
    color: black !important;
}

.blurred-text {
    filter: blur(3px);
    -webkit-filter: blur(3px);
    /* Para Safari */
    user-select: none;
    /* Evitar selección de texto cuando está borroso */
    pointer-events: none;
    /* Evitar interacción con el texto borroso */
    display: inline-block;
    /* Asegurar que el blur no afecte al layout de otros elementos */
}

.premium-explanation-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    background-color: rgba(255, 255, 255, 0.65);
    /* Overlay ligeramente más opaco */
    border-radius: 4px;
    /* Opcional: si el contenedor tiene bordes redondeados */
    padding: 8px;
    font-size: 0.85rem;
    color: #424242;
    /* Texto gris oscuro */
    box-sizing: border-box;
    /* Asegurar que el padding no agrande el overlay */
}

.premium-explanation-overlay span {
    margin-left: 6px;
    /* Espacio entre texto y botón */
    font-weight: 500;
}

.upgrade-btn-blur {
    text-transform: none !important;
    font-size: 0.8rem !important;
    font-weight: 600 !important;
    padding: 0 10px !important;
    /* Ajustar padding */
    min-width: auto !important;
    /* Permitir que el botón sea más pequeño */
    height: 30px !important;
    /* Ajustar altura */
    letter-spacing: 0.02em !important;
    border-radius: 8px !important;
}

.upgrade-btn-blur .v-icon {
    margin-right: 4px !important;
}
</style>